<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import socketClient from '@/services/socketClient';
import AudioPlayer from '@/components/crm/atendimento/AudioPlayer.vue';
import GrupoComposer from '@/components/crm/grupos/GrupoComposer.vue';
import AdicionarContatoRapidoModal from '@/components/crm/grupos/AdicionarContatoRapidoModal.vue';
import AppDatepicker from '@/components/common/AppDatepicker.vue';
import resolveUrl from '@/services/resolveUrl';
import linkify from '@/services/linkify';

function formatarHora(d) {
  if (!d) return '';
  try {
    const data = new Date(d);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === hoje.toDateString()) {
      return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (data.toDateString() === ontem.toDateString()) return 'Ontem';
    return data.toLocaleDateString('pt-BR');
  } catch (_e) { return ''; }
}

function formatarHoraCompleta(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch (_e) { return ''; }
}

function dataSeparador(d) {
  if (!d) return '';
  try {
    const data = new Date(d);
    const hoje = new Date();
    const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === hoje.toDateString()) return 'Hoje';
    if (data.toDateString() === ontem.toDateString()) return 'Ontem';
    return data.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
  } catch (_e) { return ''; }
}

function jidParaTelefone(jid) {
  return String(jid || '').split('@')[0].split(':')[0];
}

// Conversas fixadas (por navegador). Guarda uma lista ordenada de ids de grupo;
// a ordem do array É a ordem manual das fixadas (índice 0 = topo).
const PINS_KEY = 'grupos_pins';
function lerPinsSalvos() {
  try {
    const v = JSON.parse(window.localStorage.getItem(PINS_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => Number.isFinite(Number(x))).map(Number) : [];
  } catch (_e) { return []; }
}

// Canal escolhido para falar em cada grupo, quando o mesmo grupo está em mais
// de um WhatsApp conectado. Guarda `jid -> id da linha de grupo` (a linha já
// carrega o canal). Por navegador, como os pins.
const CANAL_KEY = 'grupos_canal_por_jid';
function lerCanaisSalvos() {
  try {
    const v = JSON.parse(window.localStorage.getItem(CANAL_KEY) || '{}');
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  } catch (_e) { return {}; }
}

export default {
  name: 'GruposIndex',
  components: { Layout, PageHeader, AudioPlayer, GrupoComposer, AdicionarContatoRapidoModal, AppDatepicker },
  data() {
    return {
      grupos: [],
      canais: [],
      filtros: { canal_baileys_id: '', busca: '', somenteAdmin: false, incluirArquivados: false, fixados: 'todas' },
      carregando: true,
      sincronizando: false,
      erro: null,
      sucesso: null,
      // Modal criar grupo
      modalCriar: false,
      criando: false,
      novoGrupo: { canal_baileys_id: '', nome: '', participantes: '' },
      // Chat ativo
      grupoAtivoId: null,
      grupoAtivoInfo: null,
      mensagens: [],
      carregandoMensagens: false,
      enviando: false,
      texto: '',
      // Painel info lateral (participantes/descrição)
      painelInfoAberto: false,
      live: null,
      carregandoLive: false,
      // Modal mídia
      modalMidia: false,
      midia: { tipo: 'imagem', url: '', caption: '', filename: '' },
      // Anexo selecionado pra envio (imagem/video/documento)
      arquivoSelecionado: null,
      // Gravação de áudio (MediaRecorder)
      gravacao: {
        ativa: false,
        mediaRecorder: null,
        chunks: [],
        inicio: 0,
        tempoStr: '0:00',
        timerId: null,
        stream: null,
      },
      // Retry de download de mídia: { [mensagemId]: true } enquanto em curso
      retry: {},
      // Lightbox de mídia (imagem/vídeo em tela cheia)
      lightbox: null, // { tipo: 'imagem'|'video', url: string, filename?: string }
      // Socket
      socketUnsubscribers: [],
      // Cache de links de convite por grupo (evita refetch na sessão)
      linkCache: {},
      linkCarregandoId: null,
      // Conversas fixadas (ordem manual) e menu de 3 pontos por item
      pinnedIds: lerPinsSalvos(),
      // jid -> id da linha de grupo escolhida pelo usuário para enviar.
      canalPorJid: lerCanaisSalvos(),
      menu: { id: null, top: 0, left: 0 },
      // Mini-modal "adicionar contato" a partir de um participante
      modalContato: { visivel: false, nome: '', telefone: '', username: '', jid: '' },
      // Usuário logado (pra "assumir" grupo)
      currentUserId: null,
      // Assumir/liberar grupo
      assumindo: false,
      // Modal "adicionar membro" ao grupo
      modalMembro: { visivel: false, telefone: '', enviando: false },
      // Modal de aviso (falha ao adicionar membro / mensagens gerais)
      modalAviso: { visivel: false, titulo: '', mensagem: '', tipo: 'aviso' },
      // Montagem de enquete. Duas opções em branco já na abertura porque esse
      // é o mínimo que o WhatsApp aceita.
      modalEnquete: { visivel: false, pergunta: '', opcoes: ['', ''], multipla: false, enviando: false },
      // Criação de evento (nativo do WhatsApp).
      modalEvento: {
        visivel: false, nome: '', descricao: '', inicio: '', comFim: false, fim: '',
        comLink: false, tipoLigacao: 'video', enviando: false,
      },
      // Painel de análise de enquetes do grupo.
      painelEnquetes: { visivel: false, carregando: false, lista: [], expandido: null },
    };
  },
  computed: {
    canaisConectadosIds() {
      // Set normalizando para Number — banco grava o canal_baileys_id como INT
      return new Set(this.canais.filter((c) => c.status === 'conectado').map((c) => Number(c.id)));
    },
    /**
     * Linhas visíveis agrupadas por JID.
     *
     * O sync grava UMA linha de grupo por canal: dois WhatsApps no mesmo grupo
     * viram duas linhas com o mesmo `jid`. Na tela isso aparecia como grupo
     * duplicado. Aqui as ocorrências ficam juntas e a lista mostra uma só —
     * quem escolhe por qual número falar é o seletor no topo do chat.
     */
    ocorrenciasPorJid() {
      const mapa = new Map();
      for (const g of this.grupos) {
        if (!this.canaisConectadosIds.has(Number(g.canal_baileys_id))) continue;
        if (g.participante_atual === false) continue;
        if (!mapa.has(g.jid)) mapa.set(g.jid, []);
        mapa.get(g.jid).push(g);
      }
      return mapa;
    },
    ocorrenciasDoAtivo() {
      if (!this.grupoAtivo) return [];
      return this.ocorrenciasPorJid.get(this.grupoAtivo.jid) || [];
    },
    gruposFiltrados() {
      const t = (this.filtros.busca || '').trim().toLowerCase();
      const pinnedSet = new Set(this.pinnedIds);
      let lista = this.grupos.filter((g) => {
        // Só exibe grupos cujo canal API-B está CONECTADO agora
        if (!this.canaisConectadosIds.has(Number(g.canal_baileys_id))) return false;
        // E onde o número conectado ainda é participante (admin ou membro)
        if (g.participante_atual === false) return false;
        if (!this.filtros.incluirArquivados && g.arquivado) return false;
        if (this.filtros.somenteAdmin && !g.is_admin) return false;
        if (this.filtros.canal_baileys_id && Number(g.canal_baileys_id) !== Number(this.filtros.canal_baileys_id)) return false;
        if (t && !(g.nome || '').toLowerCase().includes(t)) return false;
        return true;
      });

      // Um grupo, uma linha. A escolha sai de dentro do que já passou pelos
      // filtros — assim filtrar por canal continua mostrando aquele canal, e
      // não a ocorrência preferida de outro.
      const porJidVisivel = new Map();
      for (const g of lista) {
        if (!porJidVisivel.has(g.jid)) porJidVisivel.set(g.jid, []);
        porJidVisivel.get(g.jid).push(g);
      }
      lista = [...porJidVisivel.values()].map((ocorrencias) => this.escolherOcorrencia(ocorrencias));

      // Filtro Todas / Fixadas / Não fixadas
      if (this.filtros.fixados === 'fixadas') lista = lista.filter((g) => pinnedSet.has(g.id));
      else if (this.filtros.fixados === 'nao_fixadas') lista = lista.filter((g) => !pinnedSet.has(g.id));

      // FIXADAS no topo, em ORDEM MANUAL (índice em pinnedIds). Não reordenam
      // por mensagem recente — quem manda é a ordem definida pelas setas.
      const fixadas = lista
        .filter((g) => pinnedSet.has(g.id))
        .sort((a, b) => this.pinnedIds.indexOf(a.id) - this.pinnedIds.indexOf(b.id));

      // NÃO fixadas: ordenação normal (recência) com aninhamento de comunidades.
      const rest = lista.filter((g) => !pinnedSet.has(g.id));
      const porJid = {};
      for (const g of rest) porJid[g.jid] = g;
      const tsDe = (g) => new Date(g.ultima_mensagem_em || g.updated_at || 0).getTime();
      const raizes = rest.filter((g) => !g.comunidade_jid).sort((a, b) => tsDe(b) - tsDe(a));
      const subs = rest.filter((g) => g.comunidade_jid).sort((a, b) => tsDe(b) - tsDe(a));

      const naoFixadas = [];
      for (const raiz of raizes) {
        naoFixadas.push(raiz);
        if (raiz.eh_comunidade) {
          const filhos = subs.filter((s) => s.comunidade_jid === raiz.jid);
          for (const f of filhos) naoFixadas.push(f);
        }
      }
      const semPaiVisivel = subs.filter((s) => !porJid[s.comunidade_jid]);
      for (const s of semPaiVisivel) naoFixadas.push(s);

      return [...fixadas, ...naoFixadas];
    },
    menuGrupo() {
      return this.menu.id != null ? this.grupos.find((g) => g.id === this.menu.id) || null : null;
    },
    menuIndiceFixado() {
      return this.menu.id != null ? this.pinnedIds.indexOf(this.menu.id) : -1;
    },
    canaisConectados() {
      return this.canais.filter((c) => c.status === 'conectado');
    },
    grupoAtivo() {
      return this.grupos.find((g) => g.id === this.grupoAtivoId) || null;
    },
    isAdmin() {
      return Boolean(this.grupoAtivo && this.grupoAtivo.is_admin);
    },
    enqueteOpcoesValidas() {
      const limpas = this.modalEnquete.opcoes.map((o) => (o || '').trim()).filter(Boolean);
      const semRepetidas = new Set(limpas.map((o) => o.toLowerCase())).size === limpas.length;
      return limpas.length >= 2 && limpas.length <= 12 && semRepetidas && limpas.every((o) => o.length <= 100);
    },
    enqueteValida() {
      const p = (this.modalEnquete.pergunta || '').trim();
      return p.length > 0 && p.length <= 255 && this.enqueteOpcoesValidas;
    },
    eventoValido() {
      const e = this.modalEvento;
      if (!(e.nome || '').trim() || !e.inicio) return false;
      if (e.comFim && (!e.fim || Date.parse(e.fim) <= Date.parse(e.inicio))) return false;
      return true;
    },
    mensagensComSeparador() {
      // Inserta separador de data entre mensagens de dias diferentes
      const out = [];
      let ultimaData = '';
      for (const m of this.mensagens) {
        const d = m.mensagem_em ? new Date(m.mensagem_em).toDateString() : '';
        if (d && d !== ultimaData) {
          out.push({ separador: true, label: dataSeparador(m.mensagem_em), id: `sep-${d}` });
          ultimaData = d;
        }
        out.push(m);
      }
      return out;
    },
  },
  async mounted() {
    document.body.classList.add('grupos-sem-footer');
    this._onEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (this.menu.id != null) this.fecharMenu();
      else if (this.lightbox) this.fecharLightbox();
    };
    document.addEventListener('keydown', this._onEsc);
    // Fecha o menu de 3 pontos ao clicar fora / rolar / redimensionar.
    this._onFecharMenu = () => { if (this.menu.id != null) this.fecharMenu(); };
    document.addEventListener('click', this._onFecharMenu);
    window.addEventListener('resize', this._onFecharMenu);
    window.addEventListener('scroll', this._onFecharMenu, true);
    // Back nativo do celular: fecha o chat em vez de trocar de rota.
    this._chatHistoryPushed = false;
    this._boundPopState = this._onPopState.bind(this);
    window.addEventListener('popstate', this._boundPopState);
    await Promise.all([this.carregarCanais(), this.carregarGrupos()]);

    const u = JSON.parse(localStorage.getItem('userdata') || '{}');
    this.currentUserId = (u && u.id) || null;
    socketClient.connect(u && u.id);
    this.socketUnsubscribers.push(
      socketClient.on('baileys:grupo-mensagem', (payload) => this.onNovaMensagem(payload)),
      socketClient.on('baileys:grupo-mensagem-atualizada', (payload) => this.onMensagemAtualizada(payload)),
      socketClient.on('baileys:grupo-atribuido', (payload) => this.onGrupoAtribuido(payload)),
      socketClient.on('baileys:grupo-lido', (payload) => {
        if (!payload || !payload.grupoId) return;
        const g = this.grupos.find((x) => x.id === payload.grupoId);
        if (g) g.nao_lidas_count = 0;
      }),
      socketClient.on('baileys:grupo-enquete-voto', (payload) => this.onVotoEnquete(payload)),
      socketClient.on('baileys:status', (payload) => {
        if (!payload || !payload.canalId) return;
        const c = this.canais.find((x) => x.id === payload.canalId);
        if (c) {
          c.status = payload.status;
          if (payload.telefone) c.telefone = payload.telefone;
        }
        // Se o chat ativo é de um canal que acabou de desconectar, fecha
        if (payload.status !== 'conectado' && this.grupoAtivo && this.grupoAtivo.canal_baileys_id === payload.canalId) {
          this.fecharChat();
          this.erro = 'O canal deste grupo foi desconectado.';
        }
      }),
    );
  },
  beforeUnmount() {
    document.body.classList.remove('grupos-sem-footer');
    document.body.classList.remove('grupos-chat');
    document.body.style.overflow = '';
    if (this._onEsc) document.removeEventListener('keydown', this._onEsc);
    if (this._onFecharMenu) {
      document.removeEventListener('click', this._onFecharMenu);
      window.removeEventListener('resize', this._onFecharMenu);
      window.removeEventListener('scroll', this._onFecharMenu, true);
    }
    if (this._boundPopState) window.removeEventListener('popstate', this._boundPopState);
    this.socketUnsubscribers.forEach((fn) => { try { fn(); } catch (_e) { /* noop */ } });
  },
  methods: {
    formatarHora,
    formatarHoraCompleta,
    jidParaTelefone,
    linkify,

    async carregarCanais() {
      try {
        const { data } = await axios.get('/canal/baileys', { params: { order: JSON.stringify([['nome', 'asc']]) } });
        // Só canais marcados como tipo "operacao" entram aqui. Grupos vinculados
        // a canais sem "operacao" caem por consequência (canaisConectadosIds não
        // os inclui). Linhas com mesmo JID em outros canais aparecem normalmente.
        const lista = Array.isArray(data) ? data : [];
        this.canais = lista.filter((c) => Array.isArray(c.tipos) && c.tipos.includes('operacao'));
      } catch (error) {
        this.erro = `Falha ao listar canais: ${error.message}`;
      }
    },

    async carregarGrupos() {
      this.carregando = true;
      try {
        const params = { order: JSON.stringify([['nome', 'asc']]), limit: 500 };
        params.include = JSON.stringify([
          { model: 'CanalBaileys', as: 'canal' },
          { model: 'Usuario', as: 'atendente' },
        ]);
        const { data } = await axios.get('/grupo', { params });
        this.grupos = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao listar grupos: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregando = false;
      }
    },

    /**
     * Qual das linhas do mesmo grupo representa a conversa.
     *
     * Ordem: a que o usuário escolheu no seletor > a do canal que é admin
     * (é a única que consegue agir no grupo) > a de conversa mais recente.
     * O desempate por id mantém a lista estável entre recargas.
     */
    escolherOcorrencia(ocorrencias = []) {
      if (ocorrencias.length <= 1) return ocorrencias[0];

      const escolhidoId = Number(this.canalPorJid[ocorrencias[0].jid]);
      const escolhido = ocorrencias.find((g) => Number(g.id) === escolhidoId);
      if (escolhido) return escolhido;

      const ts = (g) => new Date(g.ultima_mensagem_em || 0).getTime();
      return [...ocorrencias].sort((a, b) => {
        if (a.is_admin !== b.is_admin) return a.is_admin ? -1 : 1;
        if (ts(a) !== ts(b)) return ts(b) - ts(a);
        return a.id - b.id;
      })[0];
    },

    /**
     * Troca o WhatsApp por onde este grupo é lido e respondido. Cada linha tem
     * seu próprio histórico (cada número só gravou o que viu), então a troca
     * recarrega as mensagens.
     */
    async trocarCanalDoGrupo(ocorrencia) {
      if (!ocorrencia || ocorrencia.id === this.grupoAtivoId) return;
      this.canalPorJid = { ...this.canalPorJid, [ocorrencia.jid]: ocorrencia.id };
      try {
        window.localStorage.setItem(CANAL_KEY, JSON.stringify(this.canalPorJid));
      } catch (_e) { /* modo privado: a escolha vale só nesta sessão */ }

      // O pin é por linha, e a troca muda a linha que representa o grupo. Sem
      // migrar o pin, fixar e depois trocar de número desfixava a conversa.
      const anterior = this.pinnedIds.indexOf(this.grupoAtivoId);
      if (anterior >= 0) {
        this.pinnedIds.splice(anterior, 1, ocorrencia.id);
        this.salvarPins();
      }

      this.grupoAtivoId = ocorrencia.id;
      this.grupoAtivoInfo = ocorrencia;
      this.mensagens = [];
      this.live = null;
      await this.carregarMensagens();
      if (this.painelInfoAberto) await this.recarregarLive();
    },

    /** Em quantos WhatsApps conectados este grupo está. 1 = sem ambiguidade. */
    canaisDoGrupo(g) {
      return (g && this.ocorrenciasPorJid.get(g.jid)) || [];
    },
    resumoCanaisDoGrupo(g) {
      const nomes = this.canaisDoGrupo(g).map((o) => this.nomeDoCanal(o));
      return `Grupo presente em ${nomes.length} números: ${nomes.join(', ')}`;
    },

    nomeDoCanal(g) {
      return (g && g.canal && (g.canal.nome || g.canal.telefone)) || 'canal';
    },

    async sincronizar() {
      if (!this.filtros.canal_baileys_id) {
        this.erro = 'Escolha um canal antes de sincronizar.';
        return;
      }
      this.sincronizando = true;
      this.erro = null;
      try {
        const { data } = await axios.post(`/grupo/sincronizar/${this.filtros.canal_baileys_id}`);
        this.sucesso = `${data.total} grupo(s) sincronizado(s).`;
        await this.carregarGrupos();
      } catch (error) {
        this.erro = `Falha ao sincronizar: ${error.response?.data?.error || error.message}`;
      } finally {
        this.sincronizando = false;
      }
    },

    _ehMobile() {
      return window.matchMedia('(max-width: 767.98px)').matches;
    },
    async abrirChat(grupo) {
      if (this.grupoAtivoId === grupo.id) return;
      const eraLista = this.grupoAtivoId == null;
      this.grupoAtivoId = grupo.id;
      this.grupoAtivoInfo = grupo;
      this.mensagens = [];
      this.live = null;
      this.painelInfoAberto = false;
      this.texto = '';
      // Conversa em fullscreen no mobile: esconde a topbar do app (via CSS).
      document.body.classList.add('grupos-chat');
      // Mobile: empurra um estado no histórico para o "voltar" nativo do
      // celular fechar o chat (voltar para a lista) em vez de trocar de rota.
      // Só empurra ao SAIR da lista (não a cada troca de chat).
      if (eraLista && this._ehMobile()) {
        window.history.pushState({ grupoChat: true }, '');
        this._chatHistoryPushed = true;
      }
      await this.carregarMensagens();
      // Scroll pro fim depois do render
      this.$nextTick(() => this.scrollChatParaFim());
      // Zera contador de não lidas no backend
      this.marcarComoLido(grupo.id);
    },

    async copiarLinkGrupo(grupo, event) {
      if (event) event.stopPropagation();
      if (!grupo || !grupo.is_admin) return;
      try {
        let info = this.linkCache[grupo.id];
        if (!info) {
          this.linkCarregandoId = grupo.id;
          const { data } = await axios.get(`/grupo/link/${grupo.id}`);
          info = data;
          this.linkCache[grupo.id] = data;
        }
        await navigator.clipboard.writeText(info.url);
        this.sucesso = `Link de "${grupo.nome || 'grupo'}" copiado.`;
      } catch (error) {
        this.erro = `Falha ao copiar link: ${error.response?.data?.error || error.message}`;
      } finally {
        this.linkCarregandoId = null;
      }
    },

    estaFixado(g) {
      return this.pinnedIds.includes(g.id);
    },
    salvarPins() {
      try { window.localStorage.setItem(PINS_KEY, JSON.stringify(this.pinnedIds)); } catch (_e) { /* noop */ }
    },
    toggleFixar(g) {
      const idx = this.pinnedIds.indexOf(g.id);
      if (idx >= 0) this.pinnedIds.splice(idx, 1);
      else this.pinnedIds.unshift(g.id); // recém-fixada vai para o topo
      this.salvarPins();
      this.fecharMenu();
    },
    moverFixado(g, dir) {
      const idx = this.pinnedIds.indexOf(g.id);
      const alvo = idx + dir;
      if (idx < 0 || alvo < 0 || alvo >= this.pinnedIds.length) return;
      const arr = this.pinnedIds.slice();
      [arr[idx], arr[alvo]] = [arr[alvo], arr[idx]];
      this.pinnedIds = arr;
      this.salvarPins();
    },
    abrirMenu(g, event) {
      event.stopPropagation();
      if (this.menu.id === g.id) { this.fecharMenu(); return; }
      const r = event.currentTarget.getBoundingClientRect();
      const LARGURA = 210;
      const left = Math.max(8, Math.min(r.right - LARGURA, window.innerWidth - LARGURA - 8));
      this.menu = { id: g.id, top: r.bottom + 4, left };
    },
    fecharMenu() {
      this.menu = { id: null, top: 0, left: 0 };
    },
    copiarLinkDoMenu() {
      if (this.menuGrupo) this.copiarLinkGrupo(this.menuGrupo);
      this.fecharMenu();
    },

    async marcarComoLido(grupoId) {
      const g = this.grupos.find((x) => x.id === grupoId);
      if (!g || !g.nao_lidas_count) return;
      try {
        await axios.post(`/grupo/marcarLido/${grupoId}`);
        g.nao_lidas_count = 0;
      } catch (_e) { /* best-effort */ }
    },

    async carregarMensagens() {
      if (!this.grupoAtivoId) return;
      this.carregandoMensagens = true;
      try {
        const { data } = await axios.get(`/grupo/mensagens/${this.grupoAtivoId}`, { params: { limit: 100 } });
        this.mensagens = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao carregar mensagens: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregandoMensagens = false;
      }
    },

    _fecharChatEstado() {
      this.grupoAtivoId = null;
      this.grupoAtivoInfo = null;
      this.mensagens = [];
      this.live = null;
      this.painelInfoAberto = false;
      document.body.classList.remove('grupos-chat');
    },
    fecharChat() {
      // Se empurramos um estado no histórico (mobile), volta por ele — o
      // popstate fecha o chat. Mantém histórico e back nativo consistentes.
      if (this._chatHistoryPushed) {
        window.history.back();
      } else {
        this._fecharChatEstado();
      }
    },
    _onPopState() {
      // Voltar nativo/navegador com um chat aberto → fecha o chat (volta à lista).
      if (this.grupoAtivoId != null) {
        this._chatHistoryPushed = false;
        this._fecharChatEstado();
      }
    },

    onNovaMensagem(payload) {
      if (!payload || !payload.grupoId) return;
      // Atualiza preview/timestamp na lista (qualquer grupo)
      const g = this.grupos.find((x) => x.id === payload.grupoId);
      if (g) {
        g.ultima_mensagem_em = payload.mensagem_em || new Date().toISOString();
        g.ultima_mensagem_preview = payload.preview || payload.conteudo || null;
        g.ultima_mensagem_tipo = payload.tipo || null;
        g.ultima_mensagem_direcao = payload.direcao || null;
        g.ultima_mensagem_autor = payload.remetente_nome || null;
        // Atualiza contador de não lidas (do backend)
        if (typeof payload.nao_lidas_count === 'number') {
          g.nao_lidas_count = payload.nao_lidas_count;
        }
      }

      if (payload.grupoId !== this.grupoAtivoId) return;

      // Se a mensagem é do grupo aberto, zera contador (já está sendo lida)
      if (g && g.nao_lidas_count && payload.direcao === 'entrada') {
        this.marcarComoLido(payload.grupoId);
      }

      // Se chegou uma saída e temos um placeholder pendente com mesmo
      // conteúdo de texto, substitui ao invés de duplicar.
      if (payload.direcao === 'saida') {
        const placeholderIdx = this.mensagens.findIndex((m) =>
          m.pendente && m.direcao === 'saida' && m.tipo === payload.tipo
          && (m.conteudo || '') === (payload.conteudo || ''));
        if (placeholderIdx >= 0) {
          this.mensagens.splice(placeholderIdx, 1, {
            id: `live-${Date.now()}-${Math.random()}`,
            grupo_id: payload.grupoId,
            direcao: payload.direcao,
            tipo: payload.tipo,
            conteudo: payload.conteudo,
            midia_url: payload.midia_url || null,
            midia_meta: payload.midia_meta || null,
            remetente_jid: payload.remetente_jid,
            remetente_nome: payload.remetente_nome,
            mensagem_em: payload.mensagem_em || new Date().toISOString(),
          });
          return;
        }
      }

      // Dedup defensivo: se já existe mensagem com mesmo external_id, pula.
      if (payload.external_id) {
        const dup = this.mensagens.find((m) => m.external_id === payload.external_id);
        if (dup) return;
      }

      this.mensagens.push({
        id: payload.id || `live-${Date.now()}-${Math.random()}`,
        grupo_id: payload.grupoId,
        direcao: payload.direcao,
        tipo: payload.tipo,
        conteudo: payload.conteudo,
        midia_url: payload.midia_url || null,
        midia_meta: payload.midia_meta || null,
        remetente_jid: payload.remetente_jid,
        remetente_nome: payload.remetente_nome,
        external_id: payload.external_id,
        mensagem_em: payload.mensagem_em || new Date().toISOString(),
      });
      this.$nextTick(() => this.scrollChatParaFim());
    },

    onMensagemAtualizada(payload) {
      if (!payload || !payload.id) return;
      const idx = this.mensagens.findIndex((m) => m.id === payload.id);
      if (idx < 0) return;
      const atual = this.mensagens[idx];
      this.mensagens.splice(idx, 1, {
        ...atual,
        midia_url: payload.midia_url || atual.midia_url,
        midia_meta: payload.midia_meta || atual.midia_meta,
      });
    },

    onVotoEnquete(payload) {
      if (!payload || !payload.mensagemId) return;
      // Atualiza o resultado no card da conversa, se a enquete está à vista.
      const idx = this.mensagens.findIndex((m) => m.id === payload.mensagemId);
      if (idx >= 0) {
        const atual = this.mensagens[idx];
        const meta = { ...(atual.midia_meta || {}) };
        if (meta.enquete) {
          meta.enquete = { ...meta.enquete, resultado: payload.resultado };
          this.mensagens.splice(idx, 1, { ...atual, midia_meta: meta });
        }
      }
      // Se o painel de análise está aberto, recarrega para refletir o voto.
      if (this.painelEnquetes.visivel) this.abrirAnaliseEnquete();
    },

    async enviarTexto() {
      if (!this.grupoAtivoId || !this.texto.trim()) return;
      const texto = this.texto.trim();
      this.enviando = true;
      // Optimistic — adiciona placeholder
      const placeholder = {
        id: `temp-${Date.now()}`,
        grupo_id: this.grupoAtivoId,
        direcao: 'saida',
        tipo: 'texto',
        conteudo: texto,
        remetente_nome: 'Eu',
        mensagem_em: new Date().toISOString(),
        pendente: true,
      };
      this.mensagens.push(placeholder);
      this.texto = '';
      this.$nextTick(() => this.scrollChatParaFim());
      try {
        await axios.post(`/grupo/mensagem/${this.grupoAtivoId}`, { texto });
        // NÃO removemos o placeholder aqui — o socket vai chegar com a
        // mensagem real e o onNovaMensagem substitui in-place. Se em 8s
        // o socket não chegar, o placeholder fica como "entregue local".
        setTimeout(() => {
          const idx = this.mensagens.findIndex((m) => m.id === placeholder.id);
          if (idx >= 0) {
            const m = this.mensagens[idx];
            if (m.pendente) this.mensagens.splice(idx, 1, { ...m, pendente: false });
          }
        }, 8000);
      } catch (error) {
        const idx = this.mensagens.findIndex((m) => m.id === placeholder.id);
        if (idx >= 0) this.mensagens.splice(idx, 1, { ...placeholder, pendente: false, erro: true });
        this.erro = `Falha ao enviar: ${error.response?.data?.error || error.message}`;
      } finally {
        this.enviando = false;
      }
    },

    onComposerEnviado() {
      // Mensagem real chega via socket; aqui não precisa fazer nada.
      // Mantemos o hook caso queiramos toast/scroll ou métricas no futuro.
      this.$nextTick(() => this.scrollChatParaFim());
    },

    abrirLightbox(tipo, midiaUrl, filename) {
      if (!midiaUrl) return;
      this.lightbox = { tipo, url: this.resolveAssetUrl(midiaUrl), filename };
      document.body.style.overflow = 'hidden';
    },
    fecharLightbox() {
      this.lightbox = null;
      document.body.style.overflow = '';
    },

    async retryDownload(item) {
      if (!item || !item.id || this.retry[item.id]) return;
      this.retry = { ...this.retry, [item.id]: true };
      try {
        const { data } = await axios.post(`/grupo/baixarMidia/${item.id}`);
        // Aplica os dados retornados na mensagem na lista
        const idx = this.mensagens.findIndex((m) => m.id === item.id);
        if (idx >= 0) {
          const novo = { ...this.mensagens[idx], midia_url: data.midia_url, midia_meta: data.midia_meta };
          this.mensagens.splice(idx, 1, novo);
        }
      } catch (error) {
        this.erro = `Falha ao baixar: ${error.response?.data?.error || error.message}`;
      } finally {
        const rest = { ...this.retry };
        delete rest[item.id];
        this.retry = rest;
      }
    },

    // === Helpers ===
    resolveAssetUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (/^(https?:|data:)/i.test(relativeUrl)) return relativeUrl;
      const base = resolveUrl(process.env.VUE_APP_APIURL) || '';
      return `${base.replace(/\/$/, '')}${relativeUrl}`;
    },
    // Imagem do card de link: usa o thumbnail salvo; se faltar, deriva do
    // YouTube (thumbnail determinístico pelo ID) — assim link do YT sempre
    // mostra imagem, independente do backend.
    linkThumb(preview) {
      if (!preview) return '';
      if (preview.thumbnail_url) return this.resolveAssetUrl(preview.thumbnail_url);
      return this.youtubeThumb(preview.canonical_url || preview.url || preview.matched_text);
    },
    youtubeThumb(url) {
      if (!url) return '';
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./i, '');
        let id = null;
        if (host === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
        else if (/(^|\.)youtube\.com$/i.test(host)) {
          id = u.searchParams.get('v') || (u.pathname.match(/\/(?:shorts|embed|live)\/([^/?#]+)/) || [])[1];
        }
        if (id && /^[\w-]{6,}$/.test(id)) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
      } catch (_e) { /* url inválida */ }
      return '';
    },
    // Mini-mapa GRÁTIS (sem chave) montado com tiles do OpenStreetMap,
    // posicionados pra centralizar a coordenada. Sem serviço externo de
    // "static map" (que é instável). Se um tile falhar, some só ele e fica o
    // fundo estilizado + pin. Devolve a lista de tiles com posição em px.
    mapaTiles(preview) {
      if (!preview || preview.latitude == null || preview.longitude == null) return [];
      const lat = Number(preview.latitude);
      const lng = Number(preview.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
      const z = 15;
      const TS = 256; // tamanho do tile
      const W = 330;
      const H = 150; // área visível do mapa
      const n = 2 ** z;
      const px = ((lng + 180) / 360) * n * TS;
      const latRad = (lat * Math.PI) / 180;
      const py = (0.5 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / (2 * Math.PI)) * n * TS;
      const originX = px - W / 2;
      const originY = py - H / 2;
      const tiles = [];
      for (let tx = Math.floor(originX / TS); tx <= Math.floor((originX + W) / TS); tx += 1) {
        for (let ty = Math.floor(originY / TS); ty <= Math.floor((originY + H) / TS); ty += 1) {
          if (ty < 0 || ty >= n) continue;
          const xt = ((tx % n) + n) % n;
          tiles.push({
            url: `https://tile.openstreetmap.org/${z}/${xt}/${ty}.png`,
            left: Math.round(tx * TS - originX),
            top: Math.round(ty * TS - originY),
          });
        }
      }
      return tiles;
    },
    // ---- Evento: formatação de data pro card ---------------------------
    eventoMes(iso) {
      if (!iso) return '';
      const meses = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
      return meses[new Date(iso).getMonth()] || '';
    },
    eventoDia(iso) {
      if (!iso) return '';
      return String(new Date(iso).getDate());
    },
    eventoQuando(evento) {
      if (!evento || !evento.inicio) return '';
      const fmt = (iso) => new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
      const fmtHora = (iso) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const ini = fmt(evento.inicio);
      if (!evento.fim) return ini;
      // Mesmo dia → mostra só a hora do término.
      const mesmoDia = new Date(evento.inicio).toDateString() === new Date(evento.fim).toDateString();
      return mesmoDia ? `${ini} – ${fmtHora(evento.fim)}` : `${ini} – ${fmt(evento.fim)}`;
    },
    // Hora sobre a MÍDIA/mapa (overlay escuro): imagem/vídeo sem legenda e
    // localização.
    metaSobrepostaDark(item) {
      if (item.tipo === 'localizacao') return true;
      if ((item.tipo === 'imagem' || item.tipo === 'video') && item.midia_url && !item.conteudo) return true;
      return false;
    },
    // Hora sobre card de corpo claro (overlay sutil): link, enquete e áudio —
    // reduz a altura tirando a linha de meta separada.
    metaSub(item) {
      if (item.tipo === 'audio' && item.midia_url) return true;
      if (item.midia_meta && (item.midia_meta.enquete || item.midia_meta.evento)) return true;
      if (item.tipo === 'texto' && item.midia_meta && item.midia_meta.link_preview) return true;
      return false;
    },
    // Qualquer overlay ativo → esconde a linha de meta separada de baixo.
    metaSobreposta(item) {
      return this.metaSobrepostaDark(item) || this.metaSub(item);
    },
    formatarTamanho(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
    detectarTipoArquivo(file) {
      const mime = file.type || '';
      if (mime.startsWith('image/')) return 'imagem';
      if (mime.startsWith('audio/')) return 'audio';
      if (mime.startsWith('video/')) return 'video';
      return 'documento';
    },
    iconePorTipo(tipo) {
      const m = { imagem: 'ri-image-line', audio: 'ri-mic-line', video: 'ri-video-line', documento: 'ri-file-line' };
      return m[tipo] || 'ri-file-line';
    },

    // === Anexo (arquivo de imagem/video/documento via clip) ===
    abrirSeletorArquivo() {
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onArquivoEscolhido(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.arquivoSelecionado = { file, tipo: this.detectarTipoArquivo(file) };
    },
    cancelarAnexo() {
      this.arquivoSelecionado = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async enviarArquivoSelecionado() {
      if (!this.arquivoSelecionado || !this.grupoAtivoId) return;
      this.enviando = true;
      try {
        const fd = new FormData();
        fd.append('file', this.arquivoSelecionado.file);
        fd.append('tipo', this.arquivoSelecionado.tipo);
        if (this.texto.trim()) fd.append('caption', this.texto.trim());
        await axios.post(`/grupo/midia/${this.grupoAtivoId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.arquivoSelecionado = null;
        this.texto = '';
      } catch (error) {
        this.erro = `Falha ao enviar mídia: ${error.response?.data?.error || error.message}`;
      } finally {
        this.enviando = false;
      }
    },
    enviarTextoOuArquivo() {
      if (this.arquivoSelecionado) return this.enviarArquivoSelecionado();
      return this.enviarTexto();
    },

    // === Gravação de áudio ===
    async iniciarGravacao() {
      if (this.gravacao.ativa) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream);
        this.gravacao.chunks = [];
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) this.gravacao.chunks.push(e.data);
        };
        mr.onstop = () => this.finalizarGravacao();
        mr.start();
        this.gravacao.mediaRecorder = mr;
        this.gravacao.stream = stream;
        this.gravacao.ativa = true;
        this.gravacao.inicio = Date.now();
        this.gravacao.tempoStr = '0:00';
        this.gravacao.timerId = setInterval(() => {
          const s = Math.floor((Date.now() - this.gravacao.inicio) / 1000);
          const m = Math.floor(s / 60);
          this.gravacao.tempoStr = `${m}:${String(s % 60).padStart(2, '0')}`;
        }, 500);
      } catch (error) {
        this.erro = `Falha ao acessar microfone: ${error.message}`;
      }
    },
    pararGravacao() {
      if (!this.gravacao.mediaRecorder) return;
      try { this.gravacao.mediaRecorder.stop(); } catch (_e) { /* noop */ }
    },
    cancelarGravacao() {
      if (this.gravacao.timerId) clearInterval(this.gravacao.timerId);
      this.gravacao.timerId = null;
      if (this.gravacao.stream) {
        this.gravacao.stream.getTracks().forEach((t) => t.stop());
      }
      this.gravacao = { ativa: false, mediaRecorder: null, chunks: [], inicio: 0, tempoStr: '0:00', timerId: null, stream: null };
    },
    async finalizarGravacao() {
      if (this.gravacao.timerId) clearInterval(this.gravacao.timerId);
      this.gravacao.timerId = null;
      if (this.gravacao.stream) {
        this.gravacao.stream.getTracks().forEach((t) => t.stop());
      }
      const chunks = this.gravacao.chunks;
      const grupoId = this.grupoAtivoId;
      this.gravacao = { ativa: false, mediaRecorder: null, chunks: [], inicio: 0, tempoStr: '0:00', timerId: null, stream: null };

      if (!chunks.length || !grupoId) return;

      const blob = new Blob(chunks, { type: 'audio/webm' });
      const file = new File([blob], `audio-${Date.now()}.webm`, { type: 'audio/webm' });
      this.enviando = true;
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`/grupo/audio/${grupoId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (error) {
        this.erro = `Falha ao enviar áudio: ${error.response?.data?.error || error.message}`;
      } finally {
        this.enviando = false;
      }
    },

    async enviarMidia() {
      if (!this.grupoAtivoId) return;
      if (!this.midia.url.trim()) { this.erro = 'Informe a URL pública.'; return; }
      const payload = { caption: this.midia.caption || undefined };
      const map = { imagem: 'imagemUrl', audio: 'audioUrl', video: 'videoUrl', documento: 'documentoUrl' };
      payload[map[this.midia.tipo]] = this.midia.url;
      if (this.midia.tipo === 'documento') payload.filename = this.midia.filename || 'arquivo';
      this.enviando = true;
      try {
        await axios.post(`/grupo/mensagem/${this.grupoAtivoId}`, payload);
        this.modalMidia = false;
        this.midia = { tipo: 'imagem', url: '', caption: '', filename: '' };
      } catch (error) {
        this.erro = `Falha: ${error.response?.data?.error || error.message}`;
      } finally {
        this.enviando = false;
      }
    },

    async abrirPainelInfo() {
      this.painelInfoAberto = true;
      if (this.live) return;
      this.carregandoLive = true;
      try {
        const { data } = await axios.get(`/grupo/live/${this.grupoAtivoId}`);
        this.live = data;
      } catch (error) {
        this.erro = `Falha ao carregar info: ${error.response?.data?.error || error.message}`;
      } finally {
        this.carregandoLive = false;
      }
    },

    async recarregarLive() {
      if (!this.grupoAtivoId) return;
      this.carregandoLive = true;
      try {
        const { data } = await axios.get(`/grupo/live/${this.grupoAtivoId}`);
        this.live = data;
      } catch (_e) { /* silencioso — o painel mantém o estado anterior */ } finally {
        this.carregandoLive = false;
      }
    },

    // -------- Assumir / liberar a conversa do grupo --------
    _aplicarGrupoAtualizado(data) {
      if (!data || !data.id) return;
      const idx = this.grupos.findIndex((g) => g.id === data.id);
      // Preserva associações que o payload não traz (ex.: canal).
      if (idx >= 0) this.grupos[idx] = { ...this.grupos[idx], ...data };
      if (this.grupoAtivoInfo && this.grupoAtivoInfo.id === data.id) {
        this.grupoAtivoInfo = { ...this.grupoAtivoInfo, ...data };
      }
    },
    onGrupoAtribuido(payload) {
      this._aplicarGrupoAtualizado(payload);
    },
    async assumirGrupo(grupo) {
      if (!grupo || this.assumindo) return;
      this.assumindo = true;
      try {
        const { data } = await axios.put(`/grupo/assumir/${grupo.id}`);
        this._aplicarGrupoAtualizado(data);
      } catch (error) {
        this.abrirAviso('Não foi possível assumir', error.response?.data?.error || error.message, 'aviso');
      } finally {
        this.assumindo = false;
      }
    },
    async liberarGrupo(grupo) {
      if (!grupo || this.assumindo) return;
      this.assumindo = true;
      try {
        const { data } = await axios.put(`/grupo/liberar/${grupo.id}`);
        this._aplicarGrupoAtualizado(data);
      } catch (error) {
        this.abrirAviso('Não foi possível liberar', error.response?.data?.error || error.message, 'aviso');
      } finally {
        this.assumindo = false;
      }
    },

    // -------- Adicionar membro ao grupo --------
    abrirAviso(titulo, mensagem, tipo = 'aviso') {
      this.modalAviso = { visivel: true, titulo, mensagem, tipo };
    },
    abrirAdicionarMembro() {
      if (!this.grupoAtivo || !this.grupoAtivo.is_admin) return;
      this.modalMembro = { visivel: true, telefone: '', enviando: false };
      this.$nextTick(() => { try { this.$refs.inputMembro && this.$refs.inputMembro.focus(); } catch (_e) { /* noop */ } });
    },
    fecharAdicionarMembro() {
      if (this.modalMembro.enviando) return;
      this.modalMembro.visivel = false;
    },
    async confirmarAdicionarMembro() {
      const grupo = this.grupoAtivo;
      const telefone = (this.modalMembro.telefone || '').trim();
      if (!grupo || !telefone || this.modalMembro.enviando) return;
      this.modalMembro.enviando = true;
      try {
        const { data } = await axios.post(`/grupo/adicionarMembro/${grupo.id}`, { telefone });
        this.modalMembro.enviando = false;
        this.modalMembro.visivel = false;
        if (data && data.ok) {
          const idx = this.grupos.findIndex((g) => g.id === grupo.id);
          if (idx >= 0) this.grupos[idx].participantes_count = (this.grupos[idx].participantes_count || 0) + 1;
          this.abrirAviso('Membro adicionado', 'O contato foi adicionado ao grupo com sucesso.', 'sucesso');
          if (this.painelInfoAberto) this.recarregarLive();
        } else {
          this.abrirAviso('Não foi possível adicionar', (data && data.mensagem) || 'O contato não pôde ser adicionado.', 'aviso');
        }
      } catch (error) {
        this.modalMembro.enviando = false;
        this.abrirAviso('Erro ao adicionar', error.response?.data?.mensagem || error.response?.data?.error || error.message, 'aviso');
      }
    },

    // ---- Enquete: leitura de resultado no card -------------------------
    resultadoEnquete(item) {
      return (item && item.midia_meta && item.midia_meta.enquete && item.midia_meta.enquete.resultado) || null;
    },
    votosDaOpcao(item, i) {
      const r = this.resultadoEnquete(item);
      return (r && Array.isArray(r.porOpcao) && r.porOpcao[i]) || 0;
    },
    totalVotantesEnquete(item) {
      const r = this.resultadoEnquete(item);
      return (r && r.totalVotantes) || 0;
    },
    pctDaOpcao(item, i) {
      const total = this.totalVotantesEnquete(item);
      if (!total) return 0;
      // % sobre votantes (não sobre votos): em múltipla escolha as barras
      // podem somar mais de 100%, que é o comportamento correto.
      return Math.round((this.votosDaOpcao(item, i) / total) * 100);
    },

    // ---- Enquete: criação ----------------------------------------------
    // Limites do próprio WhatsApp: 2 a 12 opções, 255 caracteres na pergunta e
    // 100 em cada opção. O backend revalida — isto aqui é só para o usuário
    // não descobrir no erro do envio.
    abrirEnquete() {
      this.modalEnquete = { visivel: true, pergunta: '', opcoes: ['', ''], multipla: false, enviando: false };
    },
    adicionarOpcaoEnquete() {
      if (this.modalEnquete.opcoes.length >= 12) return;
      this.modalEnquete.opcoes.push('');
    },
    removerOpcaoEnquete(i) {
      if (this.modalEnquete.opcoes.length <= 2) return;
      this.modalEnquete.opcoes.splice(i, 1);
    },
    async enviarEnquete() {
      if (!this.enqueteValida || this.modalEnquete.enviando) return;
      this.modalEnquete.enviando = true;
      try {
        await axios.post(`/grupo/enquete/${this.grupoAtivoId}`, {
          pergunta: this.modalEnquete.pergunta.trim(),
          opcoes: this.modalEnquete.opcoes.map((o) => o.trim()).filter(Boolean),
          multipla: this.modalEnquete.multipla,
        });
        this.modalEnquete.visivel = false;
        this.sucesso = 'Enquete publicada no grupo.';
        // A mensagem chega pelo eco do socket, como qualquer envio; recarregar
        // aqui só encurta a espera de quem acabou de publicar.
        await this.carregarMensagens();
      } catch (e) {
        this.abrirAviso('Não foi possível publicar', e.response?.data?.error || e.message, 'aviso');
      } finally {
        this.modalEnquete.enviando = false;
      }
    },

    // ---- Evento: criação -----------------------------------------------
    abrirEvento() {
      // Início padrão: próxima hora cheia.
      const d = new Date();
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
      const pad = (n) => String(n).padStart(2, '0');
      const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      this.modalEvento = {
        visivel: true, nome: '', descricao: '', inicio: iso, comFim: false, fim: '',
        comLink: false, tipoLigacao: 'video', enviando: false,
      };
    },
    async enviarEvento() {
      if (!this.eventoValido || this.modalEvento.enviando) return;
      this.modalEvento.enviando = true;
      try {
        await axios.post(`/grupo/evento/${this.grupoAtivoId}`, {
          nome: this.modalEvento.nome.trim(),
          descricao: (this.modalEvento.descricao || '').trim() || undefined,
          inicio: this.modalEvento.inicio,
          fim: this.modalEvento.comFim ? this.modalEvento.fim : undefined,
          comLink: this.modalEvento.comLink,
          tipoLigacao: this.modalEvento.tipoLigacao,
        });
        this.modalEvento.visivel = false;
        this.sucesso = 'Evento criado no grupo.';
        await this.carregarMensagens();
      } catch (e) {
        this.abrirAviso('Não foi possível criar o evento', e.response?.data?.error || e.message, 'aviso');
      } finally {
        this.modalEvento.enviando = false;
      }
    },

    // ---- Enquete: painel de análise ------------------------------------
    async abrirAnaliseEnquete() {
      if (!this.grupoAtivoId) return;
      this.painelEnquetes.visivel = true;
      this.painelEnquetes.carregando = true;
      try {
        const { data } = await axios.get(`/grupo/enquetes/${this.grupoAtivoId}`);
        this.painelEnquetes.lista = Array.isArray(data) ? data : [];
        // Abre a mais recente já expandida.
        this.painelEnquetes.expandido = this.painelEnquetes.lista.length ? this.painelEnquetes.lista[0].id : null;
      } catch (e) {
        this.abrirAviso('Não foi possível carregar', e.response?.data?.error || e.message, 'aviso');
      } finally {
        this.painelEnquetes.carregando = false;
      }
    },
    fecharAnaliseEnquete() {
      this.painelEnquetes.visivel = false;
    },
    toggleEnqueteExpandida(id) {
      this.painelEnquetes.expandido = this.painelEnquetes.expandido === id ? null : id;
    },
    pctAnalise(enq, i) {
      if (!enq.totalVotantes) return 0;
      return Math.round(((enq.porOpcao[i] || 0) / enq.totalVotantes) * 100);
    },

    irParaDetalhe() {
      if (!this.grupoAtivoId) return;
      this.$router.push({ path: '/app/grupos/detalhe', query: { id: this.grupoAtivoId } });
    },

    iniciarConversaCom(p) {
      if (!p.contatoId) return;
      // Nasce no módulo de Atendimento (canal oficial), reusando o fluxo/validações.
      this.$router.push({
        path: '/app/atendimento',
        query: { iniciarContatoId: p.contatoId, iniciarContatoNome: p.contatoNome || p.nome || '' },
      });
    },

    abrirAdicionarContato(p) {
      this.modalContato = {
        visivel: true,
        nome: p.nome || '',
        telefone: p.telefone || '',
        username: p.whatsappUsername || '',
        jid: p.jid || '',
      };
    },

    onContatoSalvo({ contato, jaExistia }) {
      this.sucesso = jaExistia ? 'Contato já existia — vinculado.' : 'Contato adicionado à base.';
      if (contato && this.live && Array.isArray(this.live.participants)) {
        const nome = [contato.nome, contato.sobrenome].filter(Boolean).join(' ').trim();
        const alvoTel = String(this.modalContato.telefone || '').replace(/\D/g, '');
        const alvoJid = this.modalContato.jid;
        this.live.participants.forEach((p) => {
          const casaTel = alvoTel && String(p.telefone || '').replace(/\D/g, '') === alvoTel;
          const casaJid = alvoJid && p.jid === alvoJid;
          if (casaTel || casaJid) {
            p.contatoId = contato.id;
            p.contatoNome = nome;
          }
        });
      }
    },

    abrirCriar() {
      this.novoGrupo = {
        canal_baileys_id: this.filtros.canal_baileys_id || (this.canaisConectados[0] && this.canaisConectados[0].id) || '',
        nome: '',
        participantes: '',
      };
      this.erro = null;
      this.modalCriar = true;
    },

    async confirmarCriar() {
      if (!this.novoGrupo.canal_baileys_id) { this.erro = 'Escolha um canal.'; return; }
      if (!this.novoGrupo.nome.trim()) { this.erro = 'Informe o nome.'; return; }
      const participantes = (this.novoGrupo.participantes || '')
        .split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
      if (!participantes.length) { this.erro = 'Informe ao menos um participante.'; return; }

      this.criando = true;
      try {
        await axios.post('/grupo', {
          canal_baileys_id: Number(this.novoGrupo.canal_baileys_id),
          nome: this.novoGrupo.nome.trim(),
          participantes,
        });
        this.sucesso = `Grupo "${this.novoGrupo.nome}" criado.`;
        this.modalCriar = false;
        await this.carregarGrupos();
      } catch (error) {
        const data = error.response?.data || {};
        const partes = [data.error || error.message];
        if (data.details && data.details !== data.error) partes.push(data.details);
        if (data.dica) partes.push(`Dica: ${data.dica}`);
        this.erro = `Falha ao criar grupo: ${partes.join(' — ')}`;
      } finally {
        this.criando = false;
      }
    },

    scrollChatParaFim() {
      const el = this.$refs.chatScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },

    previewMensagem(grupo) {
      const tipo = grupo.ultima_mensagem_tipo;
      const conteudo = grupo.ultima_mensagem_preview;
      if (conteudo) return conteudo;
      const labels = {
        imagem: '📷 Imagem',
        audio: '🎤 Áudio',
        video: '📹 Vídeo',
        documento: '📄 Documento',
        sticker: '😀 Sticker',
      };
      return labels[tipo] || '';
    },

    autorPreview(grupo) {
      // Para mensagens de entrada, prefixa com autor pra dar contexto.
      if (grupo.ultima_mensagem_direcao === 'entrada' && grupo.ultima_mensagem_autor) {
        return `${grupo.ultima_mensagem_autor}: `;
      }
      if (grupo.ultima_mensagem_direcao === 'saida') return 'Você: ';
      return '';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Grupos" pageTitle="Grupos" />

    <BCard no-body class="grupos-card">
      <div class="card-header flex-wrap gap-2 align-items-center d-none d-md-flex">
        <div class="flex-grow-1">
          <h5 class="m-0">Grupos WhatsApp</h5>
          <small class="text-muted">Selecione um grupo à esquerda para conversar.</small>
        </div>
        <button type="button" class="btn btn-soft-primary" :disabled="!filtros.canal_baileys_id || sincronizando" @click="sincronizar">
          <i v-if="sincronizando" class="ri-loader-4-line spin me-1"></i>
          <i v-else class="ri-refresh-line me-1"></i>
          Sincronizar
        </button>
        <button type="button" class="btn btn-success" :disabled="!canaisConectados.length" @click="abrirCriar">
          <i class="ri-add-line me-1"></i>Criar grupo
        </button>
      </div>

      <div v-if="erro" class="alert alert-danger m-2 mb-0">
        <i class="ri-error-warning-line me-1"></i>{{ erro }}
        <button class="btn-close float-end" @click="erro = null"></button>
      </div>
      <div v-if="sucesso" class="alert alert-success m-2 mb-0">
        <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
        <button class="btn-close float-end" @click="sucesso = null"></button>
      </div>

      <!-- Split layout -->
      <div class="wa-split">
        <!-- ============== Coluna esquerda: lista de grupos ============== -->
        <aside class="wa-sidebar" :class="{ 'd-none d-md-flex': grupoAtivoId }">
          <div class="wa-sidebar-header">
            <div class="wa-sidebar-busca">
              <!-- Seletor de canais: some no mobile (Criar grupo vira FAB). -->
              <div class="gap-2 align-items-center mb-2 d-none d-md-flex">
                <select v-model="filtros.canal_baileys_id" class="form-select form-select-sm flex-grow-1">
                  <option value="">Todos os canais conectados</option>
                  <option v-for="c in canaisConectados" :key="c.id" :value="c.id">{{ c.nome }}</option>
                </select>
              </div>
              <div class="wa-search-box">
                <i class="ri-search-line wa-search-icon"></i>
                <input v-model="filtros.busca" type="text" class="wa-search-input" placeholder="Pesquisar" />
              </div>
            </div>
            <div class="wa-sidebar-filtros">
              <div class="wa-chips">
                <button type="button" class="wa-chip" :class="{ active: filtros.fixados === 'todas' }" @click="filtros.fixados = 'todas'">Todas</button>
                <button type="button" class="wa-chip" :class="{ active: filtros.fixados === 'fixadas' }" @click="filtros.fixados = 'fixadas'">
                  <i class="ri-pushpin-2-fill"></i>Fixadas
                </button>
                <button type="button" class="wa-chip" :class="{ active: filtros.fixados === 'nao_fixadas' }" @click="filtros.fixados = 'nao_fixadas'">Não fixadas</button>
                <button type="button" class="wa-chip" :class="{ active: filtros.somenteAdmin }" @click="filtros.somenteAdmin = !filtros.somenteAdmin">
                  <i class="ri-shield-check-line"></i>Sou admin
                </button>
              </div>
            </div>
          </div>

          <div v-if="!canaisConectados.length" class="alert alert-warning m-2 small">
            <i class="ri-error-warning-line me-1"></i>
            Nenhum canal API-B conectado. <router-link to="/app/cadastros/canais-baileys">Conectar</router-link>
          </div>

          <div v-if="carregando" class="text-center text-muted py-4">
            <i class="ri-loader-4-line spin"></i>
          </div>
          <div v-else-if="!gruposFiltrados.length" class="text-center text-muted py-4 px-3 small">
            <i class="ri-group-line" style="font-size: 1.8rem;"></i>
            <div class="mt-2">Nenhum grupo. Sincronize um canal pra trazer os grupos do WhatsApp.</div>
          </div>
          <ul v-else class="wa-conv-list">
            <li
              v-for="g in gruposFiltrados"
              :key="g.id"
              class="wa-conv-item"
              :class="{ 'wa-conv-item--ativa': g.id === grupoAtivoId, 'wa-conv-item--nao-lida': g.nao_lidas_count > 0, 'wa-conv-item--sub': g.comunidade_jid, 'wa-conv-item--fixada': estaFixado(g) }"
              @click="abrirChat(g)"
            >
              <div class="wa-conv-avatar" :class="{ 'wa-conv-avatar--comunidade': g.eh_comunidade }">
                <img v-if="g.imagem_url" :src="resolveAssetUrl(g.imagem_url)" alt="" />
                <i v-else-if="g.eh_comunidade" class="ri-community-line"></i>
                <i v-else-if="g.eh_anuncio_comunidade" class="ri-megaphone-line"></i>
                <i v-else class="ri-group-line"></i>
              </div>
              <div class="wa-conv-body">
                <div class="d-flex justify-content-between align-items-baseline">
                  <strong class="text-truncate me-2">
                    <i v-if="g.eh_comunidade" class="ri-community-line text-primary me-1" title="Comunidade"></i>
                    <i v-else-if="g.eh_anuncio_comunidade" class="ri-megaphone-line text-warning me-1" title="Grupo de anúncios"></i>
                    {{ g.nome || '(sem nome)' }}
                  </strong>
                  <small class="wa-conv-hora flex-shrink-0" :class="{ 'text-success fw-semibold': g.nao_lidas_count > 0 }">{{ formatarHora(g.ultima_mensagem_em) }}</small>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <small class="wa-conv-preview text-truncate" :class="{ 'fw-semibold': g.nao_lidas_count > 0, 'text-muted': !g.nao_lidas_count }">
                    <template v-if="previewMensagem(g)">
                      <span v-if="g.ultima_mensagem_direcao === 'saida'" class="text-muted me-1">
                        <i class="ri-check-double-line"></i>
                      </span>
                      <span class="text-muted">{{ autorPreview(g) }}</span>{{ previewMensagem(g) }}
                    </template>
                    <template v-else>
                      <i class="ri-user-3-line me-1"></i>{{ g.participantes_count }}
                      <span v-if="g.canal && g.canal.nome"> · {{ g.canal.nome }}</span>
                    </template>
                  </small>
                  <div class="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                    <span
                      v-if="g.atendente_id"
                      class="badge wa-atendente-badge"
                      :class="g.atendente_id === currentUserId ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'"
                      :title="`Em atendimento por ${(g.atendente && g.atendente.nome) || 'operador'}`"
                    >
                      <i class="ri-user-follow-line"></i>
                      {{ g.atendente_id === currentUserId ? 'Você' : ((g.atendente && g.atendente.nome) || 'operador') }}
                    </span>
                    <span
                      v-if="canaisDoGrupo(g).length > 1"
                      class="badge bg-info-subtle text-info"
                      :title="resumoCanaisDoGrupo(g)"
                    >
                      <i class="ri-whatsapp-line"></i> {{ canaisDoGrupo(g).length }}
                    </span>
                    <i v-if="estaFixado(g)" class="ri-pushpin-2-fill wa-pin-ind" title="Conversa fixada"></i>
                    <span v-if="g.is_admin" class="badge bg-success-subtle text-success">admin</span>
                    <span v-if="g.nao_lidas_count > 0" class="wa-badge-unread">{{ g.nao_lidas_count > 99 ? '99+' : g.nao_lidas_count }}</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-link p-0 wa-menu-btn"
                      :class="{ 'wa-menu-btn--ativo': menu.id === g.id }"
                      title="Mais opções"
                      @click="abrirMenu(g, $event)"
                    >
                      <i class="ri-more-2-fill"></i>
                    </button>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </aside>

        <!-- ============== Coluna direita: chat ativo ============== -->
        <section class="wa-chat" :class="{ 'd-none d-md-flex': !grupoAtivoId }">
          <div v-if="!grupoAtivoId" class="wa-empty">
            <i class="ri-chat-3-line" style="font-size: 4rem; opacity: 0.3;"></i>
            <p class="text-muted mt-3">Selecione um grupo à esquerda para iniciar a conversa.</p>
          </div>

          <template v-else-if="grupoAtivo">
            <!-- Header do chat -->
            <header class="wa-chat-header">
              <button type="button" class="btn btn-link p-0 wa-chat-hbtn d-md-none" title="Voltar" @click="fecharChat">
                <i class="ri-arrow-left-line"></i>
              </button>
              <div class="flex-grow-1 text-truncate" @click="abrirPainelInfo" style="cursor: pointer;">
                <div class="fw-semibold text-truncate">{{ grupoAtivo.nome }}</div>
                <small class="text-muted">
                  <i class="ri-user-3-line me-1"></i>{{ grupoAtivo.participantes_count }} membros
                  <span v-if="grupoAtivo.is_admin" class="badge bg-success-subtle text-success ms-1">admin</span>
                </small>
              </div>

              <!-- Mesmo grupo em mais de um WhatsApp: por qual número falar.
                   Só aparece quando há ambiguidade de verdade. -->
              <div v-if="ocorrenciasDoAtivo.length > 1" class="dropdown me-1 flex-shrink-0">
                <button
                  type="button"
                  class="btn btn-sm btn-soft-secondary"
                  data-bs-toggle="dropdown"
                  :title="`Enviando por ${nomeDoCanal(grupoAtivo)} — clique para trocar`"
                >
                  <i class="ri-whatsapp-line"></i>
                  <span class="d-none d-sm-inline ms-1">{{ nomeDoCanal(grupoAtivo) }}</span>
                  <i class="ri-arrow-down-s-line ms-1"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><h6 class="dropdown-header">Enviar por</h6></li>
                  <li v-for="o in ocorrenciasDoAtivo" :key="o.id">
                    <a
                      class="dropdown-item d-flex align-items-center gap-2"
                      :class="{ active: o.id === grupoAtivoId }"
                      href="#"
                      @click.prevent="trocarCanalDoGrupo(o)"
                    >
                      <i :class="o.id === grupoAtivoId ? 'ri-check-line' : 'ri-whatsapp-line'"></i>
                      <span class="flex-grow-1 text-truncate">{{ nomeDoCanal(o) }}</span>
                      <span v-if="o.is_admin" class="badge bg-success-subtle text-success">admin</span>
                    </a>
                  </li>
                </ul>
              </div>

              <!-- Assumir / responsável pela conversa do grupo -->
              <button
                v-if="!grupoAtivo.atendente_id"
                type="button"
                class="btn btn-sm btn-soft-success me-1 flex-shrink-0"
                :disabled="assumindo"
                title="Assumir a conversa deste grupo"
                @click="assumirGrupo(grupoAtivo)"
              >
                <i v-if="assumindo" class="ri-loader-4-line spin"></i>
                <i v-else class="ri-user-add-line"></i>
                <span class="d-none d-sm-inline ms-1">Assumir</span>
              </button>
              <div v-else class="d-flex align-items-center gap-1 me-1 flex-shrink-0">
                <span
                  class="badge"
                  :class="grupoAtivo.atendente_id === currentUserId ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'"
                  :title="`Em atendimento por ${(grupoAtivo.atendente && grupoAtivo.atendente.nome) || 'operador'}`"
                >
                  <i class="ri-user-follow-line me-1"></i>
                  {{ grupoAtivo.atendente_id === currentUserId ? 'Você' : ((grupoAtivo.atendente && grupoAtivo.atendente.nome) || 'operador') }}
                </span>
                <button
                  v-if="grupoAtivo.atendente_id === currentUserId"
                  type="button"
                  class="btn btn-sm btn-link text-muted p-0"
                  :disabled="assumindo"
                  title="Liberar a conversa (remover responsável)"
                  @click="liberarGrupo(grupoAtivo)"
                >
                  <i class="ri-logout-box-r-line"></i>
                </button>
              </div>

              <div class="dropdown">
                <button type="button" class="btn btn-link p-0 wa-chat-hbtn" data-bs-toggle="dropdown" title="Mais opções">
                  <i class="ri-more-2-fill"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#" @click.prevent="abrirPainelInfo"><i class="ri-information-line me-1"></i>Info do grupo</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="carregarMensagens"><i class="ri-refresh-line me-1"></i>Recarregar mensagens</a></li>
                  <li><a class="dropdown-item" href="#" @click.prevent="irParaDetalhe"><i class="ri-settings-3-line me-1"></i>Gerenciar grupo</a></li>
                </ul>
              </div>
            </header>

            <!-- Mensagens -->
            <div ref="chatScroll" class="wa-chat-messages">
              <div v-if="carregandoMensagens" class="text-center text-muted py-4">
                <i class="ri-loader-4-line spin"></i>
              </div>
              <div v-else-if="!mensagens.length" class="text-center text-muted py-5 small">
                Sem mensagens ainda. Mensagens trocadas a partir de agora aparecem aqui.
              </div>
              <template v-else>
                <template v-for="item in mensagensComSeparador" :key="item.id">
                  <div v-if="item.separador" class="wa-data-sep">
                    <span>{{ item.label }}</span>
                  </div>
                  <div v-else class="wa-msg-row" :class="{ 'wa-msg-row--out': item.direcao === 'saida' }">
                    <div class="wa-msg-bubble" :class="{ 'wa-msg-bubble--out': item.direcao === 'saida', 'wa-msg-bubble--pendente': item.pendente, 'wa-msg-bubble--erro': item.erro, 'wa-msg-bubble--midia': item.midia_url && ['imagem','video','sticker'].includes(item.tipo), 'wa-msg-bubble--card': item.midia_meta && (item.midia_meta.link_preview || item.midia_meta.enquete || item.midia_meta.evento), 'wa-msg-bubble--metasub': metaSub(item) }">
                      <div v-if="item.direcao === 'entrada' && item.remetente_nome" class="wa-msg-autor">
                        {{ item.remetente_nome || jidParaTelefone(item.remetente_jid) }}
                      </div>

                      <!-- Imagem -->
                      <div v-if="item.tipo === 'imagem'" class="wa-msg-media">
                        <a v-if="item.midia_url" href="#" class="wa-msg-img-wrap" @click.prevent="abrirLightbox('imagem', item.midia_url)">
                          <img :src="resolveAssetUrl(item.midia_url)" class="wa-msg-img" alt="" />
                          <span class="wa-msg-img-zoom"><i class="ri-fullscreen-line"></i></span>
                          <span v-if="metaSobreposta(item)" class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </a>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-image-line"></i>
                          <span>Imagem indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>

                      <!-- Sticker -->
                      <div v-else-if="item.tipo === 'sticker'" class="wa-msg-media">
                        <img v-if="item.midia_url" :src="resolveAssetUrl(item.midia_url)" class="wa-msg-sticker" alt="" />
                        <div v-else class="wa-msg-tipo text-muted small"><i class="ri-emotion-line me-1"></i>Sticker</div>
                      </div>

                      <!-- Video (preview + botão de expandir) -->
                      <div v-else-if="item.tipo === 'video'" class="wa-msg-media wa-msg-video-wrap">
                        <template v-if="item.midia_url">
                          <video :src="resolveAssetUrl(item.midia_url)" preload="metadata" class="wa-msg-video"></video>
                          <button type="button" class="wa-msg-video-play" @click="abrirLightbox('video', item.midia_url)" title="Assistir em tela cheia">
                            <i class="ri-play-fill"></i>
                          </button>
                          <span v-if="metaSobreposta(item)" class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </template>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-video-line"></i>
                          <span>Vídeo indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>

                      <!-- Audio -->
                      <div v-else-if="item.tipo === 'audio'" class="wa-msg-audio-wrap">
                        <AudioPlayer v-if="item.midia_url" :src="resolveAssetUrl(item.midia_url)" :invertido="item.direcao === 'saida'" />
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-mic-line"></i>
                          <span>Áudio indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>

                      <!-- Documento -->
                      <div v-else-if="item.tipo === 'documento'" class="wa-msg-doc">
                        <a v-if="item.midia_url" :href="resolveAssetUrl(item.midia_url)" target="_blank" class="d-flex align-items-center gap-2 text-decoration-none">
                          <i class="ri-file-line wa-msg-doc-icon"></i>
                          <div class="flex-grow-1 min-w-0">
                            <div class="text-truncate fw-semibold">{{ (item.midia_meta && item.midia_meta.filename) || 'documento' }}</div>
                            <small class="text-muted">{{ formatarTamanho(item.midia_meta && item.midia_meta.size) }}</small>
                          </div>
                          <i class="ri-download-line"></i>
                        </a>
                        <div v-else class="wa-msg-falta-midia">
                          <i class="ri-file-line"></i>
                          <span>Documento indisponível</span>
                          <button type="button" class="btn btn-sm btn-link p-0" :disabled="retry[item.id]" @click="retryDownload(item)">
                            <i v-if="retry[item.id]" class="ri-loader-4-line spin"></i>
                            <i v-else class="ri-download-line"></i>
                            Tentar baixar
                          </button>
                        </div>
                      </div>

                      <!-- Localização (card com mapa estático, igual WhatsApp Web) -->
                      <a
                        v-if="item.tipo === 'localizacao' && item.midia_meta && item.midia_meta.link_preview"
                        :href="item.midia_meta.link_preview.url"
                        target="_blank"
                        class="wa-link-preview wa-loc-preview"
                      >
                        <div class="wa-loc-map">
                          <img
                            v-for="(t, i) in mapaTiles(item.midia_meta.link_preview)"
                            :key="i"
                            :src="t.url"
                            class="wa-loc-tile"
                            :style="{ left: t.left + 'px', top: t.top + 'px' }"
                            alt=""
                            loading="lazy"
                            @error="$event.target.style.visibility = 'hidden'"
                          />
                          <i class="ri-map-pin-2-fill wa-loc-pin"></i>
                          <span class="wa-meta-overlay">
                            <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                            <i v-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                          </span>
                        </div>
                      </a>

                      <!-- Link preview (texto com URL detectada) -->
                      <a
                        v-else-if="item.tipo === 'texto' && item.midia_meta && item.midia_meta.link_preview"
                        :href="item.midia_meta.link_preview.url || item.midia_meta.link_preview.canonical_url"
                        target="_blank"
                        class="wa-link-preview"
                      >
                        <img v-if="linkThumb(item.midia_meta.link_preview)" :src="linkThumb(item.midia_meta.link_preview)" class="wa-link-thumb" alt="" @error="$event.target.remove()" />
                        <div class="wa-link-body">
                          <div v-if="item.midia_meta.link_preview.title" class="wa-link-title">{{ item.midia_meta.link_preview.title }}</div>
                          <div v-if="item.midia_meta.link_preview.description" class="wa-link-desc">{{ item.midia_meta.link_preview.description }}</div>
                          <div class="wa-link-url text-truncate">{{ item.midia_meta.link_preview.canonical_url || item.midia_meta.link_preview.url }}</div>
                        </div>
                      </a>

                      <!-- Evento (nativo do WhatsApp) -->
                      <component
                        :is="item.midia_meta.evento.joinLink ? 'a' : 'div'"
                        v-if="item.midia_meta && item.midia_meta.evento"
                        :href="item.midia_meta.evento.joinLink || undefined"
                        :target="item.midia_meta.evento.joinLink ? '_blank' : undefined"
                        class="wa-evento"
                        :class="{ 'wa-evento--cancelado': item.midia_meta.evento.cancelado }"
                      >
                        <div class="wa-evento-data">
                          <span class="wa-evento-mes">{{ eventoMes(item.midia_meta.evento.inicio) }}</span>
                          <span class="wa-evento-dia">{{ eventoDia(item.midia_meta.evento.inicio) }}</span>
                        </div>
                        <div class="wa-evento-info">
                          <div class="wa-evento-nome">
                            <i class="ri-calendar-event-line me-1"></i>{{ item.midia_meta.evento.nome }}
                            <span v-if="item.midia_meta.evento.cancelado" class="badge bg-danger-subtle text-danger ms-1">Cancelado</span>
                          </div>
                          <div class="wa-evento-quando">{{ eventoQuando(item.midia_meta.evento) }}</div>
                          <div v-if="item.midia_meta.evento.descricao" class="wa-evento-desc">{{ item.midia_meta.evento.descricao }}</div>
                          <div v-if="item.midia_meta.evento.joinLink" class="wa-evento-cta">
                            <i :class="item.midia_meta.evento.tipoLigacao === 'audio' ? 'ri-phone-fill' : 'ri-vidicon-fill'"></i>
                            Entrar na {{ item.midia_meta.evento.tipoLigacao === 'audio' ? 'ligação de voz' : 'chamada de vídeo' }}
                          </div>
                        </div>
                      </component>

                      <!-- Enquete (nativa do WhatsApp). Só a montagem: os votos
                           chegam cifrados por participante e não são lidos aqui. -->
                      <div v-if="item.midia_meta && item.midia_meta.enquete" class="wa-enquete">
                        <div class="wa-enquete-cab">
                          <i class="ri-bar-chart-horizontal-line me-1"></i>
                          {{ item.midia_meta.enquete.selecionaveis > 1 ? 'Enquete · múltipla escolha' : 'Enquete · escolha única' }}
                        </div>
                        <div class="wa-enquete-pergunta">{{ item.midia_meta.enquete.pergunta }}</div>
                        <ul class="wa-enquete-opcoes">
                          <li v-for="(op, i) in item.midia_meta.enquete.opcoes" :key="i">
                            <div class="wa-enquete-op-linha">
                              <span>{{ op }}</span>
                              <span class="wa-enquete-op-num">{{ votosDaOpcao(item, i) }}</span>
                            </div>
                            <div class="wa-enquete-barra">
                              <div class="wa-enquete-barra-fill" :style="{ width: pctDaOpcao(item, i) + '%' }"></div>
                            </div>
                          </li>
                        </ul>
                        <div class="wa-enquete-rodape">
                          <span>{{ totalVotantesEnquete(item) }} {{ totalVotantesEnquete(item) === 1 ? 'pessoa votou' : 'pessoas votaram' }}</span>
                          <button type="button" class="btn btn-link btn-sm p-0 ms-2" @click.stop="abrirAnaliseEnquete">
                            Ver detalhes
                          </button>
                        </div>
                      </div>

                      <!-- Caption / texto -->
                      <div v-if="item.conteudo && (item.tipo === 'imagem' || item.tipo === 'video' || item.tipo === 'documento' || item.tipo === 'texto')" class="wa-msg-conteudo" v-html="linkify(item.conteudo)"></div>

                      <div v-if="!metaSobreposta(item)" class="wa-msg-meta">
                        <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                        <i v-if="item.pendente" class="ri-time-line ms-1"></i>
                        <i v-else-if="item.erro" class="ri-error-warning-line text-danger ms-1"></i>
                        <i v-else-if="item.direcao === 'saida'" class="ri-check-double-line ms-1"></i>
                      </div>
                      <!-- Overlay de hora p/ link/enquete/áudio (reduz altura) -->
                      <div v-if="metaSub(item)" class="wa-meta-overlay--sub">
                        <small>{{ formatarHoraCompleta(item.mensagem_em) }}</small>
                        <i v-if="item.pendente" class="ri-time-line"></i>
                        <i v-else-if="item.erro" class="ri-error-warning-line text-danger"></i>
                        <i v-else-if="item.direcao === 'saida'" class="ri-check-double-line"></i>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>

            <!-- Composer (mesmo estilo do Atendimento) -->
            <GrupoComposer
              :grupo-id="grupoAtivoId"
              @enviado="onComposerEnviado"
              @erro="(msg) => (erro = msg)"
              @abrir-enquete="abrirEnquete"
              @abrir-evento="abrirEvento"
              @abrir-midia-url="modalMidia = true"
            />
          </template>
        </section>

        <!-- ============== Painel info à direita (sliding) ============== -->
        <transition name="slide">
          <aside v-if="painelInfoAberto && grupoAtivo" class="wa-info-panel">
            <header class="wa-info-header">
              <h6 class="m-0">Info do grupo</h6>
              <button type="button" class="btn-close" @click="painelInfoAberto = false"></button>
            </header>
            <div class="wa-info-body">
              <div class="text-center py-3">
                <div class="wa-info-avatar mx-auto">
                  <img v-if="live && live.imagemUrl" :src="resolveAssetUrl(live.imagemUrl)" class="rounded-circle img-fluid" alt="" />
                  <img v-else-if="grupoAtivo.imagem_url" :src="resolveAssetUrl(grupoAtivo.imagem_url)" class="rounded-circle img-fluid" alt="" />
                  <i v-else class="ri-group-line"></i>
                </div>
                <h5 class="mt-2 mb-0">{{ grupoAtivo.nome }}</h5>
                <small class="text-muted">{{ grupoAtivo.participantes_count }} membros</small>
              </div>

              <div v-if="grupoAtivo.descricao" class="border-top pt-3 mb-3">
                <strong class="small d-block mb-1">Descrição</strong>
                <p class="small text-muted m-0">{{ grupoAtivo.descricao }}</p>
              </div>

              <div class="border-top pt-3">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <strong class="small">Participantes</strong>
                  <button
                    v-if="grupoAtivo.is_admin"
                    type="button"
                    class="btn btn-sm btn-soft-primary"
                    title="Adicionar membro ao grupo"
                    @click="abrirAdicionarMembro"
                  >
                    <i class="ri-user-add-line me-1"></i>Adicionar membro
                  </button>
                </div>
                <div v-if="carregandoLive" class="text-center text-muted py-3">
                  <i class="ri-loader-4-line spin"></i>
                </div>
                <div v-else-if="live && live.participants && live.participants.length" class="wa-info-membros">
                  <div v-for="p in live.participants" :key="p.jid" class="wa-info-membro">
                    <i class="ri-user-line text-muted flex-shrink-0"></i>
                    <div class="ms-2 flex-grow-1 min-w-0">
                      <div class="text-truncate wa-membro-nome">
                        {{ p.contatoNome || p.nome || p.telefone || 'Desconhecido' }}
                      </div>
                      <div class="text-muted text-truncate wa-membro-sub">
                        <span v-if="p.contatoId" class="badge bg-info-subtle text-info me-1">na base</span>
                        <span v-if="p.telefone && (p.contatoNome || p.nome)">{{ p.telefone }}</span>
                        <span v-else-if="!p.telefone" class="badge bg-warning-subtle text-warning">privado</span>
                      </div>
                    </div>
                    <span v-if="p.admin === 'superadmin'" class="badge bg-success-subtle text-success flex-shrink-0">Dono</span>
                    <span v-else-if="p.admin === 'admin'" class="badge bg-primary-subtle text-primary flex-shrink-0">Admin</span>
                    <!-- Ações de contato -->
                    <button
                      v-if="p.contatoId"
                      type="button"
                      class="btn btn-sm btn-soft-success flex-shrink-0 wa-membro-acao"
                      title="Iniciar conversa no Atendimento (canal oficial)"
                      @click="iniciarConversaCom(p)"
                    >
                      <i class="ri-chat-new-line"></i>
                    </button>
                    <button
                      v-else-if="p.telefone"
                      type="button"
                      class="btn btn-sm btn-soft-primary flex-shrink-0 wa-membro-acao"
                      title="Adicionar à base de contatos"
                      @click="abrirAdicionarContato(p)"
                    >
                      <i class="ri-user-add-line"></i>
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-sm btn-soft-secondary flex-shrink-0 wa-membro-acao"
                      disabled
                      title="Telefone não disponível por privacidade (username/LID do WhatsApp)"
                    >
                      <i class="ri-lock-2-line"></i>
                    </button>
                  </div>
                </div>
                <div v-else class="text-muted small">—</div>
              </div>

              <div v-if="grupoAtivo.is_admin" class="border-top pt-3 mt-3">
                <strong class="small d-block mb-2">Link de convite</strong>
                <button
                  type="button"
                  class="btn btn-sm btn-soft-primary w-100"
                  :disabled="linkCarregandoId === grupoAtivo.id"
                  @click="copiarLinkGrupo(grupoAtivo, $event)"
                >
                  <i v-if="linkCarregandoId === grupoAtivo.id" class="ri-loader-4-line spin me-1"></i>
                  <i v-else class="ri-links-line me-1"></i>
                  Copiar link do grupo
                </button>
              </div>

              <div class="text-center mt-3">
                <button type="button" class="btn btn-sm btn-soft-secondary" @click="irParaDetalhe">
                  <i class="ri-settings-3-line me-1"></i>Gerenciar grupo
                </button>
              </div>
            </div>
          </aside>
        </transition>

        <!-- Painel: análise de enquetes do grupo -->
        <transition name="slide">
          <aside v-if="painelEnquetes.visivel" class="wa-info-panel wa-enquete-panel">
            <div class="wa-info-header">
              <h6 class="m-0"><i class="ri-bar-chart-2-line me-1"></i>Enquetes do grupo</h6>
              <button type="button" class="btn-close" @click="fecharAnaliseEnquete"></button>
            </div>
            <div class="wa-info-body">
              <div v-if="painelEnquetes.carregando" class="text-center text-muted py-4">
                <i class="ri-loader-4-line spin"></i>
              </div>
              <div v-else-if="!painelEnquetes.lista.length" class="text-center text-muted py-4 small">
                <i class="ri-bar-chart-2-line" style="font-size: 1.6rem;"></i>
                <div class="mt-2">Nenhuma enquete neste grupo ainda.</div>
              </div>
              <div v-else>
                <div v-for="enq in painelEnquetes.lista" :key="enq.id" class="wa-enquete-card">
                  <button type="button" class="wa-enquete-card-cab" @click="toggleEnqueteExpandida(enq.id)">
                    <div class="flex-grow-1 text-start">
                      <div class="fw-semibold">{{ enq.pergunta }}</div>
                      <small class="text-muted">
                        {{ enq.totalVotantes }} {{ enq.totalVotantes === 1 ? 'votante' : 'votantes' }}
                        · {{ enq.multipla ? 'múltipla' : 'única' }}
                      </small>
                    </div>
                    <i :class="painelEnquetes.expandido === enq.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  </button>
                  <div v-if="painelEnquetes.expandido === enq.id" class="wa-enquete-card-corpo">
                    <div v-for="(op, i) in enq.opcoes" :key="i" class="wa-enquete-analise-op">
                      <div class="wa-enquete-op-linha">
                        <span class="text-truncate">{{ op }}</span>
                        <span class="wa-enquete-op-num">{{ enq.porOpcao[i] || 0 }} · {{ pctAnalise(enq, i) }}%</span>
                      </div>
                      <div class="wa-enquete-barra">
                        <div class="wa-enquete-barra-fill" :style="{ width: pctAnalise(enq, i) + '%' }"></div>
                      </div>
                      <div v-if="enq.votantesPorOpcao[i] && enq.votantesPorOpcao[i].length" class="wa-enquete-votantes">
                        <span v-for="(v, k) in enq.votantesPorOpcao[i]" :key="k" class="wa-enquete-votante">
                          {{ v.nome || jidParaTelefone(v.jid) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </transition>
      </div>
    </BCard>

    <!-- FAB "Criar grupo": só no mobile e apenas na lista (esconde no chat aberto) -->
    <button
      v-if="!grupoAtivoId"
      type="button"
      class="grupos-fab d-md-none"
      :disabled="!canaisConectados.length"
      title="Criar grupo"
      @click="abrirCriar"
    >
      <i class="ri-add-line"></i>
    </button>

    <!-- Modal: criar grupo -->
    <div v-if="modalCriar" class="modal-grp-overlay" @click.self="modalCriar = false">
      <div class="modal-grp-content">
        <div class="modal-grp-header">
          <h5 class="m-0">Criar grupo</h5>
          <button class="btn-close" @click="modalCriar = false"></button>
        </div>
        <div class="modal-grp-body">
          <div class="mb-3">
            <label class="form-label">Canal</label>
            <select v-model="novoGrupo.canal_baileys_id" class="form-select">
              <option value="">Selecione...</option>
              <option v-for="c in canaisConectados" :key="c.id" :value="c.id">{{ c.nome }} ({{ c.telefone }})</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Nome do grupo</label>
            <input v-model="novoGrupo.nome" type="text" class="form-control" maxlength="100" />
          </div>
          <div class="mb-2">
            <label class="form-label">Participantes</label>
            <textarea v-model="novoGrupo.participantes" rows="4" class="form-control" placeholder="55119xxxxxxxx, 55219xxxxxxxx"></textarea>
            <small class="text-muted">Formato internacional. Separe por vírgula, ponto-e-vírgula ou linha.</small>
          </div>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-light" @click="modalCriar = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="criando" @click="confirmarCriar">
            <i v-if="criando" class="ri-loader-4-line spin me-1"></i>
            {{ criando ? 'Criando...' : 'Criar grupo' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: mídia -->
    <div v-if="modalMidia" class="modal-grp-overlay" @click.self="modalMidia = false">
      <div class="modal-grp-content">
        <div class="modal-grp-header">
          <h5 class="m-0">Enviar mídia por URL</h5>
          <button class="btn-close" @click="modalMidia = false"></button>
        </div>
        <div class="modal-grp-body">
          <div class="mb-3">
            <label class="form-label">Tipo</label>
            <select v-model="midia.tipo" class="form-select">
              <option value="imagem">Imagem</option>
              <option value="audio">Áudio</option>
              <option value="video">Vídeo</option>
              <option value="documento">Documento</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">URL pública</label>
            <input v-model="midia.url" class="form-control" placeholder="https://..." />
          </div>
          <div v-if="midia.tipo === 'documento'" class="mb-3">
            <label class="form-label">Nome do arquivo</label>
            <input v-model="midia.filename" class="form-control" placeholder="arquivo.pdf" />
          </div>
          <div v-if="midia.tipo !== 'audio'" class="mb-2">
            <label class="form-label">Legenda (opcional)</label>
            <input v-model="midia.caption" class="form-control" />
          </div>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-light" @click="modalMidia = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="enviando" @click="enviarMidia">
            <i v-if="enviando" class="ri-loader-4-line spin me-1"></i>Enviar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: adicionar membro ao grupo -->
    <div v-if="modalMembro.visivel" class="modal-grp-overlay" @click.self="fecharAdicionarMembro">
      <div class="modal-grp-content">
        <div class="modal-grp-header">
          <h5 class="m-0"><i class="ri-user-add-line me-1"></i>Adicionar membro</h5>
          <button class="btn-close" @click="fecharAdicionarMembro"></button>
        </div>
        <div class="modal-grp-body">
          <p class="small text-muted">
            Informe o número com DDI e DDD. Ex: <code>5511999999999</code>. O contato
            será validado no WhatsApp antes de ser adicionado.
          </p>
          <div class="mb-2">
            <label class="form-label">Número de telefone</label>
            <input
              ref="inputMembro"
              v-model="modalMembro.telefone"
              type="tel"
              class="form-control"
              placeholder="5511999999999"
              :disabled="modalMembro.enviando"
              @keyup.enter="confirmarAdicionarMembro"
            />
          </div>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-light" :disabled="modalMembro.enviando" @click="fecharAdicionarMembro">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="modalMembro.enviando || !modalMembro.telefone" @click="confirmarAdicionarMembro">
            <i v-if="modalMembro.enviando" class="ri-loader-4-line spin me-1"></i>
            {{ modalMembro.enviando ? 'Adicionando...' : 'Adicionar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: montar enquete -->
    <div v-if="modalEnquete.visivel" class="modal-grp-overlay" @click.self="modalEnquete.visivel = false">
      <div class="modal-grp-content">
        <div class="modal-grp-header">
          <h5 class="m-0"><i class="ri-bar-chart-horizontal-line me-1"></i>Criar enquete</h5>
          <button class="btn-close" @click="modalEnquete.visivel = false"></button>
        </div>
        <div class="modal-grp-body">
          <div class="mb-3">
            <label class="form-label">Pergunta</label>
            <input
              v-model="modalEnquete.pergunta"
              type="text"
              class="form-control"
              maxlength="255"
              placeholder="Ex.: Qual o melhor dia para a reunião?"
              :disabled="modalEnquete.enviando"
            />
            <small class="text-muted">{{ (modalEnquete.pergunta || '').length }}/255</small>
          </div>

          <label class="form-label">Opções</label>
          <div v-for="(op, i) in modalEnquete.opcoes" :key="i" class="d-flex gap-2 mb-2">
            <input
              v-model="modalEnquete.opcoes[i]"
              type="text"
              class="form-control"
              maxlength="100"
              :placeholder="`Opção ${i + 1}`"
              :disabled="modalEnquete.enviando"
            />
            <button
              type="button"
              class="btn btn-outline-danger flex-shrink-0"
              :disabled="modalEnquete.opcoes.length <= 2 || modalEnquete.enviando"
              title="Remover opção"
              @click="removerOpcaoEnquete(i)"
            >
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>

          <button
            type="button"
            class="btn btn-sm btn-soft-secondary"
            :disabled="modalEnquete.opcoes.length >= 12 || modalEnquete.enviando"
            @click="adicionarOpcaoEnquete"
          >
            <i class="ri-add-line me-1"></i>Adicionar opção
            <span class="text-muted ms-1">({{ modalEnquete.opcoes.length }}/12)</span>
          </button>

          <div class="form-check form-switch mt-3">
            <input
              id="enqueteMultipla"
              v-model="modalEnquete.multipla"
              class="form-check-input"
              type="checkbox"
              :disabled="modalEnquete.enviando"
            />
            <label class="form-check-label" for="enqueteMultipla">
              Permitir marcar mais de uma opção
            </label>
          </div>

          <div v-if="grupoAtivo && !grupoAtivo.is_admin" class="alert alert-warning mt-3 mb-0 py-2 small">
            <i class="ri-alert-line me-1"></i>
            Este número não é admin do grupo. Se o grupo estiver restrito a admins, o envio será recusado.
          </div>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-light" :disabled="modalEnquete.enviando" @click="modalEnquete.visivel = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="!enqueteValida || modalEnquete.enviando" @click="enviarEnquete">
            <i v-if="modalEnquete.enviando" class="ri-loader-4-line spin me-1"></i>
            {{ modalEnquete.enviando ? 'Publicando...' : 'Publicar enquete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: criar evento -->
    <div v-if="modalEvento.visivel" class="modal-grp-overlay" @click.self="modalEvento.visivel = false">
      <div class="modal-grp-content">
        <div class="modal-grp-header">
          <h5 class="m-0"><i class="ri-calendar-event-line me-1"></i>Criar evento</h5>
          <button class="btn-close" @click="modalEvento.visivel = false"></button>
        </div>
        <div class="modal-grp-body">
          <div class="mb-3">
            <label class="form-label">Nome do evento</label>
            <input
              v-model="modalEvento.nome"
              type="text"
              class="form-control"
              maxlength="256"
              placeholder="Ex.: Reunião do diretório"
              :disabled="modalEvento.enviando"
            />
          </div>
          <div class="mb-3">
            <label class="form-label">Descrição <span class="text-muted">(opcional)</span></label>
            <textarea
              v-model="modalEvento.descricao"
              class="form-control"
              rows="2"
              placeholder="Detalhes do evento…"
              :disabled="modalEvento.enviando"
            ></textarea>
          </div>
          <div class="mb-3">
            <label class="form-label">Data e hora de início</label>
            <AppDatepicker v-model="modalEvento.inicio" mode="datetime" placeholder="Selecione…" :disabled="modalEvento.enviando" />
          </div>
          <div class="mb-3">
            <div v-if="!modalEvento.comFim">
              <button type="button" class="btn btn-sm btn-link p-0 text-decoration-none" @click="modalEvento.comFim = true">
                <i class="ri-add-line me-1"></i>Adicionar hora de término
              </button>
            </div>
            <div v-else>
              <label class="form-label d-flex align-items-center justify-content-between">
                <span>Data e hora de término</span>
                <button type="button" class="btn btn-sm btn-link p-0 text-muted" @click="modalEvento.comFim = false; modalEvento.fim = ''">
                  <i class="ri-close-line"></i>
                </button>
              </label>
              <AppDatepicker v-model="modalEvento.fim" mode="datetime" placeholder="Selecione…" :disabled="modalEvento.enviando" />
            </div>
          </div>

          <div class="form-check form-switch mb-2">
            <input id="evtLink" v-model="modalEvento.comLink" class="form-check-input" type="checkbox" :disabled="modalEvento.enviando" />
            <label class="form-check-label" for="evtLink">Link de ligação do WhatsApp</label>
          </div>
          <div v-if="modalEvento.comLink" class="mb-2 ps-4">
            <label class="form-label">Tipo de ligação</label>
            <select v-model="modalEvento.tipoLigacao" class="form-select form-select-sm" :disabled="modalEvento.enviando">
              <option value="video">📹 Vídeo</option>
              <option value="audio">📞 Voz</option>
            </select>
            <small class="text-muted d-block mt-1">O link da chamada é gerado pelo WhatsApp automaticamente.</small>
          </div>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-light" :disabled="modalEvento.enviando" @click="modalEvento.visivel = false">Cancelar</button>
          <button type="button" class="btn btn-success" :disabled="!eventoValido || modalEvento.enviando" @click="enviarEvento">
            <i v-if="modalEvento.enviando" class="ri-loader-4-line spin me-1"></i>
            {{ modalEvento.enviando ? 'Criando...' : 'Criar evento' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: aviso (falha ao adicionar membro, etc) -->
    <div v-if="modalAviso.visivel" class="modal-grp-overlay" @click.self="modalAviso.visivel = false">
      <div class="modal-grp-content modal-grp-content--sm">
        <div class="modal-grp-header">
          <h5 class="m-0">
            <i
              class="me-1"
              :class="modalAviso.tipo === 'sucesso' ? 'ri-checkbox-circle-line text-success' : 'ri-error-warning-line text-warning'"
            ></i>
            {{ modalAviso.titulo }}
          </h5>
          <button class="btn-close" @click="modalAviso.visivel = false"></button>
        </div>
        <div class="modal-grp-body">
          <p class="m-0">{{ modalAviso.mensagem }}</p>
        </div>
        <div class="modal-grp-footer">
          <button type="button" class="btn btn-primary" @click="modalAviso.visivel = false">Entendi</button>
        </div>
      </div>
    </div>

    <!-- Lightbox: visualização ampliada de imagem/vídeo -->
    <div v-if="lightbox" class="wa-lightbox" @click.self="fecharLightbox">
      <button type="button" class="wa-lightbox-fechar" @click="fecharLightbox" title="Fechar (Esc)">
        <i class="ri-close-line"></i>
      </button>
      <a
        v-if="lightbox.tipo === 'imagem'"
        :href="lightbox.url"
        :download="lightbox.filename || true"
        class="wa-lightbox-baixar"
        title="Baixar"
        @click.stop
      >
        <i class="ri-download-line"></i>
      </a>
      <img
        v-if="lightbox.tipo === 'imagem'"
        :src="lightbox.url"
        class="wa-lightbox-img"
        alt=""
      />
      <video
        v-else-if="lightbox.tipo === 'video'"
        :src="lightbox.url"
        class="wa-lightbox-video"
        controls
        autoplay
        controlsList="nodownload"
      ></video>
    </div>

    <AdicionarContatoRapidoModal
      :visivel="modalContato.visivel"
      :nome-sugerido="modalContato.nome"
      :telefone-sugerido="modalContato.telefone"
      :whatsapp-username="modalContato.username"
      :jid="modalContato.jid"
      @fechar="modalContato.visivel = false"
      @salvo="onContatoSalvo"
    />

    <!-- Menu de 3 pontos por item da lista (posição fixa, escapa do scroll) -->
    <Teleport to="body">
      <div
        v-if="menuGrupo"
        class="wa-item-menu shadow"
        :style="{ top: menu.top + 'px', left: menu.left + 'px' }"
        @click.stop
      >
        <button
          v-if="menuGrupo.is_admin"
          type="button"
          class="wa-item-menu-opt"
          :disabled="linkCarregandoId === menuGrupo.id"
          @click="copiarLinkDoMenu"
        >
          <i v-if="linkCarregandoId === menuGrupo.id" class="ri-loader-4-line spin"></i>
          <i v-else class="ri-links-line"></i>
          Copiar link do grupo
        </button>
        <button type="button" class="wa-item-menu-opt" @click="toggleFixar(menuGrupo)">
          <i :class="estaFixado(menuGrupo) ? 'ri-unpin-line' : 'ri-pushpin-2-line'"></i>
          {{ estaFixado(menuGrupo) ? 'Desafixar conversa' : 'Fixar conversa' }}
        </button>
        <template v-if="estaFixado(menuGrupo)">
          <div class="wa-item-menu-sep"></div>
          <button type="button" class="wa-item-menu-opt" :disabled="menuIndiceFixado <= 0" @click.stop="moverFixado(menuGrupo, -1)">
            <i class="ri-arrow-up-line"></i>Mover para cima
          </button>
          <button type="button" class="wa-item-menu-opt" :disabled="menuIndiceFixado < 0 || menuIndiceFixado >= pinnedIds.length - 1" @click.stop="moverFixado(menuGrupo, 1)">
            <i class="ri-arrow-down-line"></i>Mover para baixo
          </button>
        </template>
      </div>
    </Teleport>
  </Layout>
</template>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ===== Card principal cobre o viewport ===== */
.grupos-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin-bottom: 0;
}

/* ===== Split WhatsApp-Web style ===== */
.wa-split {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #f0f2f5;
  border-radius: 0 0 0.375rem 0.375rem;
  position: relative;
}
[data-bs-theme='dark'] .wa-split { background: #0b141a; }

/* ----- Sidebar (lista de grupos) ----- */
.wa-sidebar {
  --wa-bg: #fff;
  width: 384px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--vz-card-bg, #fff);
  border-right: 1px solid var(--vz-border-color);
  overflow: hidden;
}
[data-bs-theme='dark'] .wa-sidebar { --wa-bg: #111b21; }
/* Mobile: lista ocupa 100% da largura, card/painel full-bleed. */
@media (max-width: 767.98px) {
  .wa-sidebar { width: 100%; border-right: 0; }
  .wa-split { border-radius: 0; }
  .grupos-card { border-radius: 0; border-left: 0; border-right: 0; }
  .wa-info-panel { width: 100%; }
}
.wa-sidebar-header {
  background: var(--wa-bg);
}
/* Banda da busca: borda logo abaixo do campo (igual Conversas). */
.wa-sidebar-busca {
  padding: 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
}
/* Banda dos filtros/chips: sem borda até a lista. */
.wa-sidebar-filtros {
  padding: 0.75rem;
}

/* Input de busca padronizado (igual ao de Conversas/Atendimento). */
.wa-search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 35px;
  padding: 0 12px;
  border-radius: 8px;
  background: #f0f2f5;
}
.wa-search-icon { color: #54656f; font-size: 16px; flex-shrink: 0; }
.wa-search-input {
  flex: 1; min-width: 0;
  border: 0; background: transparent; outline: none;
  font-size: 13px; color: #111b21; height: 100%;
}
.wa-search-input::placeholder { color: #667781; }
[data-bs-theme='dark'] .wa-search-box { background: #202c33; }
[data-bs-theme='dark'] .wa-search-icon { color: #aebac1; }
[data-bs-theme='dark'] .wa-search-input { color: #e9edef; }
[data-bs-theme='dark'] .wa-search-input::placeholder { color: #8696a0; }

.wa-conv-list {
  list-style: none;
  margin: 0; padding: 0;
  overflow-y: auto;
  flex: 1;
}
.wa-conv-item {
  display: flex;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
  cursor: pointer;
  transition: background 0.1s;
  align-items: center;
}
.wa-conv-item:hover { background: var(--vz-light, #f5f6f7); }
[data-bs-theme='dark'] .wa-conv-item:hover { background: rgba(255,255,255,0.04); }
.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, #e7f1ff); }
[data-bs-theme='dark'] .wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, rgba(151, 18, 25, 0.16)); }

.wa-conv-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: var(--vz-primary-bg-subtle, #e9ecef);
  color: var(--vz-primary, #0d6efd);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 1.3rem;
  overflow: hidden;
}
.wa-conv-avatar img { width: 100%; height: 100%; object-fit: cover; }
.wa-conv-body { flex: 1; min-width: 0; }
.wa-conv-preview { max-width: 100%; }
.wa-conv-hora { color: var(--vz-text-muted, #878a99); font-size: 0.72rem; }

.wa-conv-item--sub { padding-left: 1.6rem; position: relative; }
.wa-conv-item--sub::before {
  content: '';
  position: absolute;
  left: 0.7rem;
  top: 50%;
  width: 8px; height: 8px;
  border-left: 2px solid var(--vz-border-color);
  border-bottom: 2px solid var(--vz-border-color);
  border-bottom-left-radius: 4px;
  transform: translateY(-50%);
  opacity: 0.6;
}
.wa-conv-avatar--comunidade {
  background: linear-gradient(135deg, #5664f5, #25d366) !important;
  color: #fff !important;
}
.wa-conv-item--nao-lida { background: rgba(0, 150, 100, 0.04); }
[data-bs-theme='dark'] .wa-conv-item--nao-lida { background: rgba(11, 178, 124, 0.08); }
.wa-conv-item--nao-lida.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, #e7f1ff); }
[data-bs-theme='dark'] .wa-conv-item--nao-lida.wa-conv-item--ativa { background: var(--vz-primary-bg-subtle, rgba(151, 18, 25, 0.16)); }

.wa-badge-unread {
  display: inline-flex;
  min-width: 20px; height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #25d366;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* Chips de filtro (mesmo estilo do Atendimento) */
.wa-chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  flex-wrap: nowrap;
  scrollbar-width: none;            /* Firefox: sem barra */
  -ms-overflow-style: none;         /* Edge legado: sem barra */
  -webkit-overflow-scrolling: touch; /* swipe com inércia (iOS) */
  overscroll-behavior-x: contain;
}
.wa-chips::-webkit-scrollbar { display: none; width: 0; height: 0; }
.wa-chip {
  border: none;
  border-radius: 18px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  background: #e9edef;
  color: #667781;
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.wa-chip:hover { background: #d1d7db; }
.wa-chip.active {
  background: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.15);
  color: var(--brand-primary, #971219);
}
[data-bs-theme='dark'] .wa-chip { background: #202c33; color: #8696a0; }
[data-bs-theme='dark'] .wa-chip:hover { background: #374045; }
[data-bs-theme='dark'] .wa-chip.active { background: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.2); color: var(--brand-primary, #971219); }

.wa-menu-btn {
  color: var(--vz-secondary-color, #878a99);
  line-height: 1;
  opacity: 0.5;
  transition: opacity 0.12s;
}
.wa-conv-item:hover .wa-menu-btn,
.wa-menu-btn--ativo { opacity: 0.9; }
.wa-menu-btn:hover, .wa-menu-btn--ativo { color: var(--vz-primary, #0d6efd); opacity: 1; }
.wa-pin-ind { color: var(--vz-secondary-color, #878a99); font-size: 0.85rem; }
.wa-conv-item--fixada { background: rgba(var(--vz-primary-rgb, 13, 110, 253), 0.055); }
.wa-conv-item--fixada:hover { background: rgba(var(--vz-primary-rgb, 13, 110, 253), 0.075); }
.wa-conv-item--fixada.wa-conv-item--ativa { background: rgba(var(--vz-primary-rgb, 13, 110, 253), 0.095); }

/* FAB "Criar grupo" (mobile) — padronizado com o de Notícias/Canais. */
.grupos-fab {
  position: fixed;
  right: 1.25rem;
  /* Acima da barra de navegação inferior (58px) + respiro + safe-area. */
  bottom: calc(58px + 1rem + env(safe-area-inset-bottom, 0px));
  z-index: 1045;
  width: 56px; height: 56px;
  border: 0; border-radius: 50%;
  background: var(--vz-primary, #971219);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.7rem; line-height: 1;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
}
.grupos-fab:hover { background: var(--brand-primary-hover, #7a0e14); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35); transform: translateY(-1px); }
.grupos-fab:active { transform: translateY(0); }
.grupos-fab:disabled { opacity: 0.55; box-shadow: none; cursor: default; }

/* Menu de 3 pontos (Teleport, posição fixa) */
.wa-item-menu {
  position: fixed;
  z-index: 1080;
  min-width: 210px;
  background: var(--vz-body-bg, #fff);
  color: var(--vz-body-color);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.5rem;
  padding: 0.35rem;
}
.wa-item-menu-opt {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  padding: 0.5rem 0.6rem;
  border-radius: 0.35rem;
  font-size: 0.875rem;
  cursor: pointer;
}
.wa-item-menu-opt:hover:not(:disabled) { background: var(--vz-tertiary-bg, rgba(0, 0, 0, 0.05)); }
.wa-item-menu-opt:disabled { opacity: 0.45; cursor: default; }
.wa-item-menu-sep { height: 1px; background: var(--vz-border-color); margin: 0.3rem 0; }

/* ----- Painel chat ----- */
.wa-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--vz-card-bg, #fff);
  overflow: hidden;
  min-width: 0;
}
.wa-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.wa-chat-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-light, #f8f9fa);
  flex-shrink: 0;
}
[data-bs-theme='dark'] .wa-chat-header { background: var(--vz-light, #1f2937); }
/* Botões de voltar e 3 pontos: mesmo tamanho do 3-pontos da topbar (42px, fs-22). */
.wa-chat-header .wa-chat-hbtn {
  width: 42px; height: 42px;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.375rem; line-height: 1;
  color: var(--vz-secondary-color);
}

/* Mobile: header da conversa com as cores da topbar (vermelho PCdoB),
   adaptando o amarelo ao modo escuro igual à topbar. */
@media (max-width: 767.98px) {
  .wa-chat-header {
    background: var(--vz-primary, #971219);
    border-bottom-color: rgba(0, 0, 0, 0.18);
  }
  [data-bs-theme='dark'] .wa-chat-header {
    background: var(--brand-primary-dark, #3f060a);
    border-bottom-color: rgba(0, 0, 0, 0.35);
  }
  .wa-chat-header .fw-semibold,
  .wa-chat-header .text-muted,
  .wa-chat-header .btn-link,
  .wa-chat-header i {
    color: var(--vz-yellow, #fff100) !important;
  }
  [data-bs-theme='dark'] .wa-chat-header .fw-semibold,
  [data-bs-theme='dark'] .wa-chat-header .text-muted,
  [data-bs-theme='dark'] .wa-chat-header .btn-link,
  [data-bs-theme='dark'] .wa-chat-header i {
    color: var(--vz-yellow, #f2c94c) !important;
  }
}

.wa-chat-avatar {
  width: 40px; height: 40px;
  border-radius: 50%;
  background: var(--vz-primary-bg-subtle, #e9ecef);
  color: var(--vz-primary, #0d6efd);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.wa-chat-avatar img { width: 100%; height: 100%; object-fit: cover; }

.wa-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  /* Mesmo fundo do Atendimento (doodles PCdoB em SVG). `local` faz o padrão
     acompanhar o scroll e repetir por toda a altura da conversa. */
  background-color: #fcf8f4;
  background-image: url('~@/assets/img/chat-bg-light.svg');
  background-repeat: repeat;
  background-size: 280px 280px;
  background-attachment: local;
}
[data-bs-theme='dark'] .wa-chat-messages {
  background-color: #03060a;
  background-image: url('~@/assets/img/chat-bg-dark.svg');
}

.wa-data-sep {
  display: flex; justify-content: center; margin: 0.75rem 0;
}
.wa-data-sep span {
  background: rgba(255, 255, 255, 0.85);
  color: #555;
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  text-transform: capitalize;
}
[data-bs-theme='dark'] .wa-data-sep span {
  background: rgba(255, 255, 255, 0.06);
  color: #ccc;
}

.wa-msg-row {
  display: flex;
  margin-bottom: 0.4rem;
}
.wa-msg-row--out { justify-content: flex-end; }

.wa-msg-bubble {
  max-width: 70%;
  background: #fff;
  padding: 0.45rem 0.7rem 0.3rem;
  border-radius: 8px;
  box-shadow: 0 1px 0.5px rgba(0,0,0,0.13);
  position: relative;
  word-wrap: break-word;
}
/* Balão da MINHA mensagem: tom claro da cor primária (segue o whitelabel via
   --vz-primary; fallback fixo pra navegador sem color-mix). */
.wa-msg-bubble--out {
  background: #f6e1e2;
  background: color-mix(in srgb, var(--vz-primary, #971219) 13%, #fff);
}
[data-bs-theme='dark'] .wa-msg-bubble {
  background: #202c33;
  color: #e9edef;
  box-shadow: 0 1px 0.5px rgba(0,0,0,0.4);
}
[data-bs-theme='dark'] .wa-msg-bubble--out {
  background: #3d2529;
  background: color-mix(in srgb, var(--vz-primary, #971219) 42%, #1b2429);
  color: #e9edef;
}
.wa-msg-bubble--pendente { opacity: 0.7; }
.wa-msg-bubble--erro { background: #ffe0e0; }
[data-bs-theme='dark'] .wa-msg-bubble--erro { background: #5c2424; }

.wa-msg-autor {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f7aec;
  margin-bottom: 0.15rem;
}
.wa-msg-conteudo {
  font-size: 0.92rem;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}
/* URL longa (sem espaços) quebra dentro do balão em vez de estourar a largura. */
.wa-msg-conteudo :deep(a) { color: #027eb5; text-decoration: underline; overflow-wrap: anywhere; word-break: break-all; }
/* Link na minha mensagem: tom da primária, legível sobre o balão claro/escuro. */
.wa-msg-bubble--out .wa-msg-conteudo :deep(a) { color: var(--vz-primary, #971219); }
[data-bs-theme='dark'] .wa-msg-conteudo :deep(a) { color: #53bdeb; }
[data-bs-theme='dark'] .wa-msg-bubble--out .wa-msg-conteudo :deep(a) { color: #f0aeb4; }
.wa-msg-meta {
  text-align: right;
  font-size: 0.7rem;
  color: rgba(0,0,0,0.45);
  margin-top: 0.15rem;
}
[data-bs-theme='dark'] .wa-msg-meta { color: rgba(255,255,255,0.45); }
.wa-msg-tipo { margin-bottom: 0.2rem; }

/* Hora sobreposta na mídia/mapa (estilo WhatsApp): faixa de largura TOTAL no
   rodapé, gradiente de baixo pra cima + hora branca à direita. */
.wa-msg-img-wrap, .wa-msg-video-wrap { position: relative; }
.wa-meta-overlay {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  color: #fff; font-size: 0.68rem; line-height: 1;
  padding: 20px 8px 5px;
  background: linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 45%, transparent 100%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.65);
  pointer-events: none;
}
.wa-meta-overlay i { font-size: 0.85rem; }

/* Variante para cards de corpo claro (link, enquete, áudio): a hora ocupa a
   linha de baixo à direita, sobre um gradiente sutil — remove a linha de meta
   separada e reduz a altura do card. */
.wa-meta-overlay--sub {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 3;
  display: flex; justify-content: flex-end; align-items: flex-end; gap: 3px;
  font-size: 0.68rem; line-height: 1; color: var(--vz-secondary-color, #8696a0);
  padding: 16px 0.6rem 0.35rem;
  background: linear-gradient(to top, var(--vz-tertiary-bg) 20%, transparent 100%);
  pointer-events: none; border-radius: 0 0 8px 8px;
}
[data-bs-theme='dark'] .wa-meta-overlay--sub { color: #8696a0; }
.wa-msg-bubble--out .wa-meta-overlay--sub { background: linear-gradient(to top, color-mix(in srgb, var(--vz-primary, #971219) 13%, #fff) 20%, transparent 100%); }
[data-bs-theme='dark'] .wa-msg-bubble--out .wa-meta-overlay--sub { background: linear-gradient(to top, color-mix(in srgb, var(--vz-primary, #971219) 42%, #1b2429) 20%, transparent 100%); }
.wa-meta-overlay--sub i { font-size: 0.8rem; }
/* Reserva o rodapé do balão pro overlay --sub (link/enquete/áudio). */
.wa-msg-bubble--metasub { padding-bottom: 1.05rem; }
.wa-msg-bubble--card.wa-msg-bubble--metasub { padding-bottom: 1.05rem; }

.wa-msg-bubble--midia { padding: 0.25rem 0.25rem 0.3rem; max-width: 360px; }
.wa-msg-bubble--midia .wa-msg-conteudo { padding: 0 0.45rem; }
.wa-msg-bubble--midia .wa-msg-meta { padding: 0 0.5rem 0; }

/* Cards que não são texto puro (link, localização, enquete): largura padrão
   do balão (igual WhatsApp Web). O card fica quase rente às bordas (padding
   lateral mínimo), e só a URL/hora abaixo ganham um respiro pequeno. */
.wa-msg-bubble--card { width: 330px; max-width: 100%; padding: 0.3rem 0.3rem 0.35rem; }
.wa-msg-bubble--card .wa-link-preview { max-width: none; width: 100%; }
.wa-msg-bubble--card .wa-msg-conteudo,
.wa-msg-bubble--card .wa-msg-meta { padding-left: 0.4rem; padding-right: 0.4rem; }
.wa-msg-media { display: flex; justify-content: center; }
.wa-msg-img {
  max-width: 100%; max-height: 320px; border-radius: 6px; cursor: zoom-in;
  display: block;
}
.wa-msg-video {
  max-width: 100%; max-height: 320px; border-radius: 6px;
  display: block;
}
.wa-msg-img-wrap {
  position: relative;
  display: inline-block;
  text-decoration: none;
  border-radius: 6px;
  overflow: hidden;
}
.wa-msg-img-zoom {
  position: absolute;
  top: 8px; right: 8px;
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;
  pointer-events: none;
}
.wa-msg-img-wrap:hover .wa-msg-img-zoom { opacity: 1; }
.wa-msg-img-wrap:hover .wa-msg-img-zoom:hover { background: rgba(0, 0, 0, 0.75); }

.wa-msg-video-wrap { position: relative; display: inline-block; }
.wa-msg-video-play {
  position: absolute; inset: 0; margin: auto;
  width: 64px; height: 64px;
  border-radius: 50%; border: 0;
  background: rgba(0,0,0,0.55); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 2rem; cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}
.wa-msg-video-play:hover { background: rgba(0,0,0,0.75); transform: scale(1.05); }
.wa-msg-sticker { max-width: 160px; max-height: 160px; }
.wa-msg-audio-wrap { min-width: 240px; padding: 0.2rem 0.1rem; }

/* ===== Override do AudioPlayer estilo WhatsApp Web (apenas dentro do balão de grupo) ===== */
.wa-msg-audio-wrap :deep(.audio-player) { gap: 0.15rem; }
.wa-msg-audio-wrap :deep(.audio-topo) {
  gap: 0.55rem;
  min-height: 36px;
}
.wa-msg-audio-wrap :deep(.audio-rodape) {
  min-height: 14px;
  margin-top: -2px;
  padding-left: 38px; /* alinha com o início da waveform */
}

/* Botão play — WhatsApp usa cinza esverdeado neutro, não primary do projeto */
.wa-msg-audio-wrap :deep(.btn-play) {
  width: 30px; height: 30px;
  background: #54656f !important;
  color: #fff !important;
  box-shadow: none;
}
.wa-msg-audio-wrap :deep(.audio-player.invertido .btn-play) {
  background: #fff !important;
  color: #54656f !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.btn-play) {
  background: #aebac1 !important;
  color: #111b21 !important;
}
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.audio-player.invertido .btn-play) {
  background: #aebac1 !important;
  color: #111b21 !important;
}

/* Waveform — barras WhatsApp style */
.wa-msg-audio-wrap :deep(.waveform) { height: 22px; gap: 2px; }
.wa-msg-audio-wrap :deep(.bar) {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 2px;
}
.wa-msg-audio-wrap :deep(.bar.ativa) { background: #54656f; }
.wa-msg-audio-wrap :deep(.audio-player.invertido .bar) { background: rgba(0, 0, 0, 0.18); }
.wa-msg-audio-wrap :deep(.audio-player.invertido .bar.ativa) { background: #54656f; }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.bar) { background: rgba(255, 255, 255, 0.18); }
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.bar.ativa) { background: #aebac1; }

/* Tempo — fonte menor e mais discreta */
.wa-msg-audio-wrap :deep(.tempo) {
  font-size: 0.7rem;
  opacity: 0.7;
  min-width: 30px;
}

/* Chip de velocidade — bem pequeno, alinhado embaixo da waveform */
.wa-msg-audio-wrap :deep(.btn-velocidade) {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.62rem;
  padding: 0.05rem 0.45rem;
  border-radius: 9px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.wa-msg-audio-wrap :deep(.btn-velocidade:hover) { background: rgba(0, 0, 0, 0.14); }
.wa-msg-audio-wrap :deep(.btn-velocidade.destaque) {
  background: #54656f;
  color: #fff;
}
.wa-msg-audio-wrap :deep(.audio-player.invertido .btn-velocidade) {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.55);
}
.wa-msg-audio-wrap :deep(.audio-player.invertido .btn-velocidade.destaque) {
  background: #54656f;
  color: #fff;
}
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.btn-velocidade) {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}
[data-bs-theme='dark'] .wa-msg-audio-wrap :deep(.btn-velocidade.destaque) {
  background: #aebac1;
  color: #111b21;
}

.wa-msg-doc { padding: 0.25rem; }
.wa-msg-doc a {
  background: rgba(0, 0, 0, 0.04);
  color: inherit;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}
[data-bs-theme='dark'] .wa-msg-doc a { background: rgba(255, 255, 255, 0.06); color: #e9edef; }
.wa-msg-doc a:hover { background: rgba(0, 0, 0, 0.07); }
[data-bs-theme='dark'] .wa-msg-doc a:hover { background: rgba(255, 255, 255, 0.1); }
.wa-msg-doc-icon { font-size: 1.7rem; opacity: 0.8; }

.wa-msg-falta-midia {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.6rem;
  background: rgba(0,0,0,0.04);
  border-radius: 6px;
  font-size: 0.85rem;
  color: var(--vz-text-muted, #878a99);
}
[data-bs-theme='dark'] .wa-msg-falta-midia { background: rgba(255,255,255,0.06); }
.wa-msg-falta-midia button { color: var(--vz-primary, #0d6efd) !important; }

.wa-link-preview {
  display: flex;
  flex-direction: column;
  margin: 0.1rem 0 0.35rem;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0,0,0,0.05);
  color: inherit;
  text-decoration: none !important;
  max-width: 330px;
  transition: background 0.15s;
}
[data-bs-theme='dark'] .wa-link-preview { background: rgba(255,255,255,0.07); }
.wa-link-preview:hover { background: rgba(0,0,0,0.08); }
[data-bs-theme='dark'] .wa-link-preview:hover { background: rgba(255,255,255,0.11); }
.wa-link-thumb {
  width: 100%; aspect-ratio: 1.91 / 1; max-height: 188px;
  object-fit: cover; display: block; background: rgba(0,0,0,0.06);
}
[data-bs-theme='dark'] .wa-link-thumb { background: rgba(255,255,255,0.05); }
.wa-link-body { padding: 0.5rem 0.7rem 0.55rem; }
.wa-link-title {
  font-weight: 600; font-size: 0.9rem; line-height: 1.3; margin-bottom: 0.1rem;
  color: var(--vz-body-color, #1a1d21);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-desc {
  font-size: 0.8rem; color: var(--vz-secondary-color, #878a99); line-height: 1.3; margin-bottom: 0.15rem;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-link-url {
  font-size: 0.72rem; color: var(--vz-secondary-color, #878a99); text-transform: lowercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.wa-loc-map {
  position: relative;
  width: 100%; height: 140px;
  display: flex; align-items: center; justify-content: center;
  /* Fundo estilizado (fallback quando o mapa estático não carrega). */
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #cfe8d6, #a9d2c3);
  overflow: hidden;
}
[data-bs-theme='dark'] .wa-loc-map {
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 26px),
    linear-gradient(135deg, #223129, #1a2b33);
}
.wa-loc-tile { position: absolute; width: 256px; height: 256px; max-width: none; user-select: none; pointer-events: none; }
.wa-loc-pin {
  position: relative; z-index: 1;
  font-size: 2.2rem; color: #e11d48;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));
  transform: translateY(-4px);
}
.wa-loc-preview .wa-link-url { color: var(--vz-secondary-color, #878a99); }

.wa-preview-anexo {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: var(--vz-light, #f8f9fa);
  border: 1px dashed var(--vz-border-color);
  border-radius: 8px;
  margin-bottom: 0.4rem;
}
[data-bs-theme='dark'] .wa-preview-anexo { background: rgba(255, 255, 255, 0.04); }
.min-w-0 { min-width: 0; }
.gravando-pulse { animation: pulse 1s infinite; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

/* ----- Composer ----- */
.wa-chat-composer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  background: var(--vz-light, #f8f9fa);
  border-top: 1px solid var(--vz-border-color);
  flex-shrink: 0;
}
[data-bs-theme='dark'] .wa-chat-composer { background: var(--vz-light, #1f2937); }

.wa-input {
  resize: none;
  max-height: 120px;
  border-radius: 22px;
  padding: 0.5rem 1rem;
}

/* ----- Painel info ----- */
.wa-info-panel {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 340px;
  background: var(--vz-card-bg, #fff);
  border-left: 1px solid var(--vz-border-color);
  display: flex;
  flex-direction: column;
  z-index: 10;
  box-shadow: -4px 0 12px rgba(0,0,0,0.1);
}
.wa-info-header {
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-light);
}
.wa-info-body { padding: 1rem; overflow-y: auto; flex: 1; }
.wa-info-avatar {
  width: 96px; height: 96px;
  border-radius: 50%;
  background: var(--vz-primary-bg-subtle, #e9ecef);
  color: var(--vz-primary, #0d6efd);
  display: flex; align-items: center; justify-content: center;
  font-size: 2.6rem;
  overflow: hidden;
}
.wa-info-membros { max-height: 360px; overflow-y: auto; }
.wa-info-membro {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--vz-border-color);
  font-size: 0.9rem;
}
.wa-info-membro .min-w-0 { min-width: 0; }
.wa-membro-nome { font-weight: 500; }
.wa-membro-sub { font-size: 0.75rem; }
.wa-membro-acao { padding: 0.15rem 0.4rem; line-height: 1; }

.slide-enter-active, .slide-leave-active { transition: transform 0.2s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(100%); }

/* ----- Lightbox ----- */
.wa-lightbox {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex; align-items: center; justify-content: center;
  z-index: 1090;
  padding: 1.5rem;
}
.wa-lightbox-img { max-width: 95vw; max-height: 95vh; border-radius: 4px; }
.wa-lightbox-video {
  max-width: 95vw; max-height: 95vh; background: #000; border-radius: 4px;
  outline: none;
}
.wa-lightbox-fechar, .wa-lightbox-baixar {
  position: absolute; top: 1rem;
  width: 44px; height: 44px;
  border-radius: 50%; border: 0;
  background: rgba(255, 255, 255, 0.12); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; cursor: pointer; text-decoration: none;
  transition: background 0.15s;
}
.wa-lightbox-fechar { right: 1rem; }
.wa-lightbox-baixar { right: 4rem; }
.wa-lightbox-fechar:hover, .wa-lightbox-baixar:hover { background: rgba(255, 255, 255, 0.25); color: #fff; }

/* ----- Modals ----- */
.modal-grp-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1080;
}
.modal-grp-content {
  background: var(--vz-card-bg, #fff);
  color: var(--vz-body-color, inherit);
  border-radius: 12px;
  width: 100%; max-width: 480px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  border: 1px solid var(--vz-border-color, transparent);
}
[data-bs-theme="dark"] .modal-grp-content { background: var(--vz-card-bg, #2a2f3a); }
.modal-grp-header, .modal-grp-footer {
  padding: 1rem 1.25rem;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid var(--vz-border-color);
}
.modal-grp-footer {
  border-bottom: none; border-top: 1px solid var(--vz-border-color);
  justify-content: flex-end; gap: 0.5rem;
}
.modal-grp-body { padding: 1.25rem; }
.modal-grp-content--sm { max-width: 400px; }

/* Card de evento (estilo calendário) */
.wa-evento {
  display: flex; gap: 0.7rem; align-items: flex-start;
  padding: 0.55rem; border-radius: 8px; margin: 0.1rem 0;
  background: rgba(0,0,0,0.04); color: inherit; text-decoration: none !important;
}
[data-bs-theme='dark'] .wa-evento { background: rgba(255,255,255,0.06); }
a.wa-evento:hover { background: rgba(0,0,0,0.07); }
[data-bs-theme='dark'] a.wa-evento:hover { background: rgba(255,255,255,0.1); }
.wa-evento-data {
  flex-shrink: 0; width: 46px; border-radius: 7px; overflow: hidden;
  display: flex; flex-direction: column; align-items: center; text-align: center;
  background: var(--vz-primary, #971219); color: #fff;
}
.wa-evento-mes { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.04em; padding: 2px 0; background: rgba(0,0,0,0.2); width: 100%; }
.wa-evento-dia { font-size: 1.3rem; font-weight: 700; line-height: 1; padding: 4px 0 5px; }
.wa-evento-info { min-width: 0; flex: 1; }
.wa-evento-nome { font-weight: 600; font-size: 0.9rem; }
.wa-evento-quando { font-size: 0.78rem; color: var(--vz-secondary-color, #878a99); margin-top: 1px; }
.wa-evento-desc {
  font-size: 0.78rem; color: var(--vz-secondary-color, #878a99); margin-top: 3px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.wa-evento-cta {
  margin-top: 6px; display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.8rem; font-weight: 600; color: var(--vz-primary, #971219);
}
.wa-evento--cancelado .wa-evento-nome { text-decoration: line-through; opacity: 0.7; }

/* Enquete na conversa. Cores por token: a bolha muda de fundo entre os temas
   e entre entrada/saída, então nada de cor fixa aqui. */
.wa-enquete {
  border: 1px solid var(--vz-border-color);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.35rem;
  background: color-mix(in srgb, var(--vz-body-color) 5%, transparent);
}
.wa-enquete-cab {
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.03em;
  opacity: 0.65; margin-bottom: 0.35rem;
}
.wa-enquete-pergunta { font-weight: 600; margin-bottom: 0.6rem; }
.wa-enquete-opcoes { list-style: none; margin: 0; padding: 0; }
.wa-enquete-opcoes li { padding: 0.35rem 0; font-size: 0.9rem; }
.wa-enquete-op-linha {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.5rem; margin-bottom: 0.2rem;
}
.wa-enquete-op-num { font-size: 0.8rem; opacity: 0.7; flex-shrink: 0; }
.wa-enquete-barra {
  height: 6px; border-radius: 4px; overflow: hidden;
  background: color-mix(in srgb, var(--vz-body-color) 12%, transparent);
}
.wa-enquete-barra-fill {
  height: 100%; border-radius: 4px;
  background: var(--vz-success, #0ab39c);
  transition: width 0.3s ease;
}
.wa-enquete-rodape {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 0.72rem; opacity: 0.75; margin-top: 0.5rem;
}

/* Painel de análise de enquetes */
.wa-enquete-panel { z-index: 11; }
.wa-enquete-card {
  border: 1px solid var(--vz-border-color);
  border-radius: 10px; margin-bottom: 0.6rem; overflow: hidden;
}
.wa-enquete-card-cab {
  width: 100%; display: flex; align-items: center; gap: 0.5rem;
  padding: 0.6rem 0.75rem; border: none; cursor: pointer;
  background: color-mix(in srgb, var(--vz-body-color) 4%, transparent);
  color: inherit;
}
.wa-enquete-card-corpo { padding: 0.5rem 0.75rem 0.75rem; }
.wa-enquete-analise-op { margin-bottom: 0.7rem; }
.wa-enquete-votantes {
  display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.35rem;
}
.wa-enquete-votante {
  font-size: 0.72rem; padding: 0.1rem 0.45rem; border-radius: 10px;
  background: color-mix(in srgb, var(--vz-body-color) 8%, transparent);
}
.wa-atendente-badge {
  display: inline-flex; align-items: center; gap: 2px;
  max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-weight: 500;
}
</style>

<style>
/* Modo full-viewport — mesma técnica do Atendimento */
body.grupos-sem-footer { overflow: hidden; }
body.grupos-sem-footer .footer-pcdob,
body.grupos-sem-footer .footer { display: none !important; }
body.grupos-sem-footer .main-content {
  height: 100vh;
  height: 100dvh; /* mobile: desconta a barra do navegador — footer/composer sempre visível */
  display: flex; flex-direction: column; overflow: hidden;
}
body.grupos-sem-footer .page-content {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  overflow: hidden; padding-bottom: 0;
}
body.grupos-sem-footer .page-content > .container-fluid {
  flex: 1; min-height: 0;
  display: flex; flex-direction: column;
  overflow: hidden;
}
body.grupos-sem-footer .page-content > .container-fluid > div:first-child { flex-shrink: 0; }

/* ===== Mobile: comportamento de app nativo (estilo WhatsApp) =====
   (as regras de .wa-sidebar/.wa-split/.grupos-card/.wa-info-panel ficam no
   bloco SCOPED — aqui elas perderiam em especificidade para a base scoped.) */
@media (max-width: 767.98px) {
  /* Full-bleed: remove o respiro lateral (padding ~12px do container E do
     page-content) e esconde o cabeçalho de página (breadcrumb). */
  body.grupos-sem-footer .page-content,
  body.grupos-sem-footer .page-content > .container-fluid {
    padding-left: 0; padding-right: 0;
  }
  body.grupos-sem-footer .page-content > .container-fluid > div:first-child { display: none; }
  /* Cola a lista logo abaixo da topbar (mesma altura do header de conversa). */
  body.grupos-sem-footer .page-content { padding-top: 60px; }

  /* Conversa em fullscreen: esconde a topbar do app e cola o conteúdo no topo. */
  body.grupos-chat #page-topbar { display: none; }
  body.grupos-chat.grupos-sem-footer .page-content { padding-top: 0 !important; }
}
</style>
